/* eslint-disable class-methods-use-this */
import axios, { AxiosInstance, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { Store } from 'vuex';
import type { RootState } from '@/interfaces/store';
import type {
    MassBalanceConfig,
    MassBalanceFlowMeterData,
    Analysis as MassBalanceAnalysis,
    SiteProduct,
    MassBalancePayload,
} from '@/interfaces/massBalanceInterface';
import type { DevicesResponse } from '@/interfaces/devices';
import HTTP_STATUS_CODES from '@/aquassist-shared/constants/httpStatusCode';

// Extended AxiosRequestConfig to include retry flag
interface AxiosRequestConfigWithRetry extends InternalAxiosRequestConfig {
    _retry?: boolean;
}

declare module 'axios' {
    export interface AxiosRequestConfig {
        suppressErrorToastStatuses?: number[];
    }
}
import type {
    ConsolidatedReportPayload,
    ConsolidatedReportResponse,
} from '@/interfaces/apiInterfaces';
import { Socket, io } from 'socket.io-client';
import { cloneDeep } from 'lodash';

import store from '@/store';
import showError from '@/store/modules/errorHandler';
import ChatConstants from '@/constants/chatHelper';
import {
    disconnectSocketClient,
    getSocketClient,
    initSocketClient,
    reconnectSocketClient,
} from '@/utilities/socket';
import {
    AccessGroup,
    ComponentConfiguration,
    LabAnalysisTypeConfiguration,
    payloadClientInformation,
    responseGetClientInformation,
    QueryParams,
    responseGetClientMembers,
    WorkOrder,
    SitesResponse,
    Team,
    Feature,
    AnalysisRequest,
    Product,
    TreatmentProgramInterface,
    RiskAnalysis,
    RiskAnalysisResponse,
    NetworkOrComponentState,
    AnalysisType,
    Analysis,
    StandardDeepCopy,
    LegionellaRiskAssessmentQuestion,
    Client,
    Site,
    Contact,
    Network,
    E4BoilerApiResponse,
    E4CoolingApiResponse,
    ReleaseNote,
    DisplayNoteData,
    StandardFamily,
} from '@/interfaces/common';
import {
    AccessGroupDetail,
    ActivityTypesResponse,
    AddItemToOrderPayload,
    AddProductInventoryPayload,
    AddClientAccessPayload,
    AddSeriePayload,
    AssociateSiteProductPayload,
    NotificationsResponse,
    NotificationDetailsResponse,
    AddSentenceCategoryPayload,
    AnalysisCategory,
    AnalysisCategoriesResponse,
    AnalysisTypePayload,
    DeviceAnalysisKeysResponse,
    AvailableProbesResponse,
    AnalysisConfigurationResponse,
    ContactFunctionDetail,
    ContactFunctionListResponse,
    AnalysisGroupsResponse,
    AnalysisRequestTemplatesResponse,
    ComponentTemplatesResponse,
    AnalysisRequestsResponse,
    ARTemplateByComponentResponse,
    AutomaticSentencePayload,
    AutomaticSentencesResponse,
    ChatApiResponse,
    ClientAccessResponse,
    ClientTeamMemberPayload,
    ComponentTreatmentResponse,
    ConfigurationInfo,
    ContactFormData,
    ContactsResponse,
    ControllerHubPayload,
    ContractsResponse,
    CouponsResponse,
    CopySitePayload,
    ComponentConfigurationResponse,
    CustomError,
    DefaultAnalysesResponse,
    DefaultAnalysesStatsResponse,
    E4EconomyType,
    FlowDiagramsResponse,
    FlowMeterCreatePayload,
    FlowMeterDetailUpdatePayload,
    FlowMeterPayload,
    FlowMeterUpdatePayload,
    FlowDiagramContainer,
    FuelMeterPayload,
    FlowMeterRatePayload,
    GetAnalysisTypesResponse,
    GetAutomaticSentencesParams,
    getLabBillingAnalysisRequest,
    GetCertificatesHistoryResponse,
    GetComponentTypesResponse,
    GetComponentsForAnalysisType,
    GetContactsParams,
    GetProductsParams,
    GetMemberAccessRes,
    GetMembersParams,
    GetCertificatesResponse,
    GetOrdersParams,
    GetParamsStandard,
    GetProceduresResponse,
    GetSentenceCategoriesParams,
    GetStandardFamiliesParams,
    GetStandardFamiliesRes,
    GetStandardsParams,
    GetStandardsRes,
    GetCouponsParams,
    StandardPayload,
    StandardFamilyPayload,
    GetSiteChangeEventsParams,
    GetSiteReportsParams,
    GraphicsResponse,
    GetProductInventoryItemParams,
    GetProductInventoryParams,
    GetProductParams,
    GetFrequenciesRes,
    GetActivitiesRes,
    GetControlChart,
    GetTeamsParams,
    GetTeamsRes,
    TeamPayload,
    GetSiteAlertsParams,
    GetClientsRes,
    GetDocuments,
    GetEventsResponse,
    GetEventResponse,
    EventItemResponse,
    GraphicItem,
    ProductLevelPayload,
    ProductsResponse,
    InvitedMemberResponse,
    InviteMemberPayload,
    ListResponse,
    LegionellaControlChartsResponse,
    LegionellaControlChart,
    LegionellaRiskAssessmentPayload,
    LegionellaRiskAssessmentResponse,
    LoginResponse,
    Member,
    MembersResponse,
    MeasureHistory,
    MeasureHistoryResponse,
    NetworkWithAnalyses,
    NodeTreeResponse,
    ProfileDetails,
    ProbeUpdatePayload,
    ProductDetailResponse,
    ProductInventoryItem,
    ProductInventoryResponse,
    ProfilePayload,
    ReportType,
    RiskAnalysisPayload,
    ScheduleCategoriesRes,
    ScheduleTemplateRes,
    SentenceCategoriesResponse,
    SeriesResponse,
    SiteChangeEventsResponse,
    SiteNotificationsResponse,
    SiteReportLabWorkOrder,
    SiteReportWorkOrder,
    SiteReportsResponse,
    SiteInventoryProductsResponse,
    SiteOrdersResponse,
    SiteProductsResponse,
    SiteAlertsResponse,
    SiteAlert,
    SiteDocument,
    SocketAuthData,
    State,
    Country,
    TreatmentResponse,
    UpdateMemberAccessGroupPayload,
    UpdateMemberPayload,
    UpdateMembersAIActivationPayload,
    UserLoginData,
    UpdatedProductQuantity,
    UpdatedSiteOrder,
    UpdateProductInventoryPayload,
    UpdateSentenceCategoryPayload,
    UpdateSiteProductPayload,
    UserSignupData,
    WorkOrderTemplateInfoResponse,
    WorkOrderTemplatesResponse,
    GetSiteDailySummaryRes,
    GetCurrentBillingParams,
    CurrentBillingSummaryItem,
    GetCurrentBillingExportRes,
    GetCurrentBillingExportPayload,
    CurrentBillingDetailsParams,
    GetCurrentBillingDetailsRes,
    ProductConsumptionWorkOrdersResponse,
} from '@/interfaces/apiInterfaces';
import { DashboardTemplateTypes, ServiceCallType } from '@/views/sites/dashboard/interfaces';
import { LabBillingType } from '@/interfaces/lab';
import { ServiceWorkOrderBillingResponse } from '@/interfaces/serviceWO';
import nodesType from '@/utilities/enums/nodeTypes';
import type { ControlChartPayload, ReorderSiteNodesPayload } from '@/interfaces/site-tree.types';
import {
    Comment,
    LastReading,
    MeasureNoteResponse,
    Devices,
    ProductInventory,
    Table,
    NetworkAnalysisData,
} from '@/views/sites/workorder/Workorder.types';
import { ScheduleData, SchedulePayload } from '@/views/maintenanceSchedule/utils';
import { ChartAction } from '@/interfaces/common';
import { SensorItem } from '@/interfaces/devices/Sensors.types';
import type {
    QueryParams as AnalysisOverviewQueryParams,
    GetSiteAnalysisOverviewRes,
} from '@/views/sites/dashboard/widgets/AnalysisOverview/types';

const { SOCKET_EVENTS } = ChatConstants;

let isRefreshing = false;
let refreshPromise: Promise<void> | null = null;
let failedQueue: Array<{
    resolve: (value: void | PromiseLike<void>) => void;
    reject: (reason?: unknown) => void;
}> = [];

function getNodeType(nodeType: string): string {
    let nodeEndpoint = '';
    switch (nodeType.toLowerCase()) {
        case nodesType.site:
            nodeEndpoint = '/sites';
            break;
        case nodesType.container:
            nodeEndpoint = '/containers';
            break;
        case nodesType.network:
            nodeEndpoint = '/networks';
            break;
        case nodesType.component:
            nodeEndpoint = '/components';
            break;
        default:
    }

    return nodeEndpoint;
}

type NetworkAnalysisBulkEntry = NetworkAnalysisData & { networkRef: string };

class AqAjaxRequests {
    private socket!: Socket | null;
    private reconnectionAttempts!: number;
    private appId!: string;
    private appVersion!: string;
    private appPlatform!: string;
    private url!: string;
    private axiosRequest!: AxiosInstance;
    private axiosAuthentifiedRequest!: AxiosInstance;
    private $store!: Store<RootState>;
    private networkAnalysisInFlight: Map<string, Promise<Map<string, NetworkAnalysisData>>> =
        new Map();

    get authClient(): AxiosInstance {
        return this.axiosAuthentifiedRequest;
    }

    async initSocket(): Promise<Socket> {
        if (this.socket?.connected) {
            console.log('Socket is already initialized');
            return this.socket;
        }

        if (!this.socket) {
            await this.initializeAndConnectSocket();
        } else {
            this.reconnectWithBackoff();
        }

        return new Promise<Socket>((resolve, reject) => {
            if (this.socket?.connected) {
                resolve(this.socket);
            } else if (this.socket) {
                this.socket.once('connect', () => resolve(this.socket as Socket));
                this.socket.once('connect_error', (error: Error) => reject(error));
            } else {
                reject(new Error('Socket is null'));
            }
        });
    }

    async initializeAndConnectSocket(): Promise<void> {
        try {
            const response: ChatApiResponse = await this.getChatApiInfo();
            const { endpointRTM, tokenId, tokenSecret } = response.data;
            const endpoint = new URL(endpointRTM);
            this.socket = io(endpoint.origin, {
                path: endpoint.pathname || '/',
                transports: ['websocket'],
            });

            this.setupSocketListeners(tokenId, tokenSecret);
        } catch (error) {
            console.error('Failed to initialize and connect socket:', error);
            throw error;
        }
    }

    setupSocketListeners(tokenId: string, tokenSecret: string): void {
        if (!this.socket) return;

        this.socket.on('connect', () => {
            console.log('RTM API connected');
            this.authenticateSocket(tokenId, tokenSecret);
            this.reconnectionAttempts = 0;
        });

        this.socket.on('disconnect', () => {
            console.log('RTM API disconnected');
            this.reconnectWithBackoff();
        });

        this.socket.on('reconnect', () => {
            console.log('RTM API reconnected');
            this.authenticateSocket(tokenId, tokenSecret);
        });

        this.socket.on('connect_error', (error: Error) => {
            console.log('RTM API connection error', error);
        });

        this.socket.on('error', (error: Error) => {
            console.error('RTM API error', error);
        });
    }

    reconnectWithBackoff(maxAttempts = 5): void {
        const attemptReconnection = (): void => {
            if (this.reconnectionAttempts < maxAttempts && this.socket) {
                setTimeout(() => {
                    if (this.socket && !this.socket.connected) {
                        console.log(
                            `Attempting to reconnect (${
                                this.reconnectionAttempts + 1
                            }/${maxAttempts})...`,
                        );
                        this.reconnectionAttempts += 1;
                        this.socket.connect();
                        attemptReconnection();
                    }
                }, this.calculateExponentialBackoff(this.reconnectionAttempts));
            } else {
                console.log('Max reconnection attempts reached. Stopping attempts to reconnect.');
            }
        };

        attemptReconnection();
    }

    calculateExponentialBackoff(attempts: number, baseDelay = 1000): number {
        return Math.min(30000, baseDelay * 2 ** attempts);
    }

    authenticateSocket(tokenId: string, tokenSecret: string): void {
        if (!this.socket) {
            console.error('Socket is not initialized');
            return;
        }

        const authData: SocketAuthData = {
            username: tokenId,
            password: tokenSecret,
            events: Object.values(SOCKET_EVENTS),
        };

        this.socket.emit('authentication', authData);
    }

    init(): void {
        // $rq rejects on failure and the interceptor has already shown the toast, so a caller
        // needs try/catch only to recover — never just to report the error.
        if (typeof window !== 'undefined') {
            const win = window as Window & { __aqUnhandledRejectionPatched?: boolean };
            if (!win.__aqUnhandledRejectionPatched) {
                window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
                    const reason = event.reason as Partial<AxiosError & { __aqHandled?: boolean }>;
                    if (reason && reason.__aqHandled) {
                        event.preventDefault(); // swallow so the red overlay will not appear
                    }
                });
                // Also swallow regular error events for tagged errors
                window.addEventListener('error', (event: ErrorEvent) => {
                    const errObj = event.error as
                        | Partial<AxiosError & { __aqHandled?: boolean }>
                        | undefined;
                    if (errObj && errObj.__aqHandled) {
                        event.preventDefault();
                    }
                });
                win.__aqUnhandledRejectionPatched = true;
            }
        }

        this.$store = store;
        this.socket = null;
        this.reconnectionAttempts = 0;
        this.appId = process.env.VUE_APP_APP_ID as string;
        this.appVersion = process.env.PACKAGE_VERSION as string;
        this.appPlatform = 'web';
        this.url = process.env.VUE_APP_API_URL as string;
        this.axiosRequest = axios.create({
            baseURL: this.url,
        });
        this.axiosAuthentifiedRequest = axios.create({
            baseURL: this.url,
        });

        this.setAxiosHeaders({
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache',
            'X-AQ-App-Identifier': this.appId,
            'X-AQ-App-Version': this.appVersion,
            'X-AQ-Platform': this.appPlatform,
            'X-AQ-Locale-Identifier': this.$store.getters.lang,
            'X-AQ-Framework-version': 'v3', // Pour que le backend sache si on utilise la version avec Vue2 ou La nouvelle en Vue3
        });

        const accessToken = window.$cookies?.get('accessToken');

        if (accessToken) {
            this.axiosAuthentifiedRequest.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
        }

        this.axiosAuthentifiedRequest.interceptors.request.use(
            async (request: InternalAxiosRequestConfig<unknown>) => {
                axios.defaults.headers.common['X-AQ-Locale-Identifier'] = this.$store.getters.lang;
                if (window.$cookies?.isKey('accessToken')) {
                    request.headers = request.headers || {};
                    request.headers.Authorization = `Bearer ${window.$cookies?.get('accessToken')}`;

                    return request;
                }

                await this.refreshAccessTokenRequestAndResponseInterceptor();

                request.headers = request.headers || {};
                request.headers.Authorization = `Bearer ${window.$cookies?.get('accessToken')}`;

                return request;
            },
        );

        this.axiosAuthentifiedRequest.interceptors.response.use(
            (response: AxiosResponse) => Promise.resolve(response),
            async (error: CustomError) => {
                const originalRequest = (error as AxiosError).config;

                if (!axios.isCancel(error) && originalRequest) {
                    // Check for 401 Unauthorized or 403 Forbidden with token-related errors
                    const isTokenError =
                        (error as CustomError).response?.status ===
                            HTTP_STATUS_CODES.UNAUTHORIZED ||
                        ((error as CustomError).response?.status === HTTP_STATUS_CODES.FORBIDDEN &&
                            (error as CustomError)?.response?.data?.error?.message
                                ?.toLowerCase()
                                .includes('token'));

                    if (isTokenError && !(originalRequest as AxiosRequestConfigWithRetry)._retry) {
                        // Mark request as retried to prevent infinite loops
                        (originalRequest as AxiosRequestConfigWithRetry)._retry = true;

                        try {
                            await this.refreshAccessTokenRequestAndResponseInterceptor();
                            // Update the authorization header with the new token
                            const newToken = window.$cookies?.get('accessToken');
                            if (newToken) {
                                originalRequest.headers = originalRequest.headers ?? {};
                                originalRequest.headers.Authorization = `Bearer ${newToken}`;
                                return this.axiosAuthentifiedRequest.request(originalRequest);
                            }
                        } catch (refreshError) {
                            // If refresh fails, logout and redirect
                            this.$store.dispatch('logout').then(() => window.location.reload());
                            return Promise.reject(refreshError);
                        }
                    }

                    // Handle other errors (non-token related)
                    if ((error as CustomError)?.response?.data?.error?.code === 1002) {
                        return Promise.reject(error as AxiosError);
                    }

                    const status = (error as CustomError).response?.status;
                    if (status && originalRequest.suppressErrorToastStatuses?.includes(status)) {
                        (error as CustomError).__aqHandled = true;
                        return Promise.reject(error);
                    }
                }

                // The error is displayed here and then rethrown: a caller must never continue into
                // its success path on failure. `__aqHandled` lets the global listener swallow the
                // rejection when nobody catches it, so no overlay appears (see init()).
                error.__aqHandled = true;
                await showError(error as AxiosError);
                return Promise.reject(error);
            },
        );
        // Automatically show errors if not authenticated
        this.axiosRequest.interceptors.response.use(
            (response: AxiosResponse) => response,
            async (error: AxiosError) => {
                (error as CustomError).__aqHandled = true;
                await showError(error);
                return Promise.reject(error);
            },
        );

        const socketClient = getSocketClient();
        const isLoggedIn = store.state.auth.isLoggedIn;
        if (!socketClient && isLoggedIn) {
            this.initWebSockets();
        }
    }

    async getChatApiInfo(): Promise<ChatApiResponse> {
        // This method appears to be used but not defined in the original code
        // Implementing a placeholder that would need to be filled with actual implementation
        throw new Error('getChatApiInfo method not implemented');
    }

    refreshAccessTokenRequestAndResponseInterceptor(): Promise<void> {
        // If already refreshing, return a promise that resolves when refresh is complete
        if (isRefreshing) {
            return new Promise((resolve, reject) => {
                failedQueue.push({ resolve, reject });
            });
        }

        const refreshToken = window.$cookies?.get('refreshToken');
        if (!refreshToken) {
            return this.$store.dispatch('logout').then(() => window.location.reload());
        }

        // Check if refresh token is expired
        try {
            const payload = JSON.parse(window.atob(refreshToken.split('.')[1]));
            const currentTime = Math.floor(Date.now() / 1000);
            if (payload.exp && payload.exp < currentTime) {
                return this.$store.dispatch('logout').then(() => window.location.reload());
            }
        } catch (error) {
            return this.$store.dispatch('logout').then(() => window.location.reload());
        }

        isRefreshing = true;
        refreshPromise = this.$store
            .dispatch('refreshAccessTokenAndReloadAccessControlState')
            .then(() => {
                isRefreshing = false;
                // Process all failed requests that were queued
                failedQueue.forEach(({ resolve }) => resolve());
                failedQueue = [];
            })
            .catch((err: unknown) => {
                isRefreshing = false;
                failedQueue.forEach(({ reject }) => reject(err));
                failedQueue = [];
                return this.$store.dispatch('logout').then(() => window.location.reload());
            });

        return refreshPromise ?? Promise.resolve();
    }

    setAxiosHeaders(headers: Record<string, string>): void {
        Object.entries(headers).forEach(([key, value]) => {
            this.axiosRequest.defaults.headers.common[key] = value;
            this.axiosAuthentifiedRequest.defaults.headers.common[key] = value;
        });
    }

    resetAxiosHeaders(): void {
        Object.keys(axios.defaults.headers.common).forEach((headerKey) => {
            delete this.axiosRequest.defaults.headers.common[headerKey];
            delete this.axiosAuthentifiedRequest.defaults.headers.common[headerKey];
        });
        delete this.axiosAuthentifiedRequest.defaults.headers.common.Authorization;
    }

    async initWebSockets(): Promise<void> {
        await initSocketClient();
    }

    reconnectWebSockets(): void {
        reconnectSocketClient();
    }

    disconnectWebSockets(): void {
        disconnectSocketClient();
    }

    getRenewToken(teamId?: string): Promise<string> {
        return new Promise<string>((resolve, reject) => {
            const refreshToken = window.$cookies?.get('refreshToken');

            if (!refreshToken) {
                this.$store.dispatch('logout').then(() => window.location.reload());
                reject(new Error('No refresh token available'));
                return;
            }

            // Validate refresh token format and expiration
            try {
                const payload = JSON.parse(window.atob(refreshToken.split('.')[1]));
                const currentTime = Math.floor(Date.now() / 1000);

                if (payload.exp && payload.exp < currentTime) {
                    this.$store.dispatch('logout').then(() => window.location.reload());
                    reject(new Error('Refresh token is expired'));
                    return;
                }
            } catch (tokenError) {
                this.$store.dispatch('logout').then(() => window.location.reload());
                reject(new Error('Invalid refresh token format'));
                return;
            }

            // Prepare request data with team context
            const data: { refreshToken: string; rtype: string; teamId?: string } = {
                refreshToken,
                rtype: 'auth',
            };

            // Add team ID if available
            const currentTeamId = teamId || this.$store.getters.getTeamID;

            if (currentTeamId) {
                data.teamId = currentTeamId;
            }

            const axiosWithoutInterceptors = axios.create({
                baseURL: this.url,
                timeout: 10000,
            });

            axiosWithoutInterceptors
                .post(`${this.url}/auth/token`, data, {
                    headers: {
                        'Authorization': `Bearer ${refreshToken}`,
                        'Content-Type': 'application/json',
                    },
                })
                .then((response: AxiosResponse) => {
                    if (!response?.data?.accessToken) {
                        throw new Error('Invalid token renewal response');
                    }

                    const newToken = response.data.accessToken;

                    // Update cookies and store
                    window.$cookies?.set('accessToken', newToken, 30, true);

                    this.$store.commit({
                        type: 'setAccessToken',
                        token: newToken,
                        secured: process.env.NODE_ENV !== 'development',
                    });

                    // Update Axios headers immediately
                    this.setAxiosHeaders({ Authorization: `Bearer ${newToken}` });

                    // Initialize websockets with new token
                    this.initWebSockets().catch((wsError) => {
                        console.warn(
                            'WebSocket initialization failed after token renewal:',
                            wsError,
                        );
                    });

                    resolve(newToken);
                })
                .catch((error: AxiosError) => {
                    this.$store.dispatch('logout').then(() => window.location.reload());
                    reject(error);
                });
        });
    }

    login(user: UserLoginData): Promise<LoginResponse> {
        const endpoint = '/auth/login';
        return this.axiosRequest
            .post(endpoint, user)
            .then((response: AxiosResponse) => response.data);
    }

    loginSso(payload: object): Promise<AxiosResponse> {
        const endpoint = '/sso';
        return this.axiosAuthentifiedRequest.post(endpoint, payload);
    }

    getVerificationCodeStatus(verificationCode: string, userId: string): Promise<AxiosResponse> {
        const endpoint = `/users/${userId}/verification/verified?verificationcode=${verificationCode}`;
        return this.axiosRequest.get(endpoint);
    }

    postUserSignup(userData: UserSignupData) {
        const endpoint = `/users/${userData.id}/verification/confirm?verificationcode=${userData.verificationCode}`;

        return this.axiosRequest.post(endpoint, {
            firstName: userData.firstName,
            lastName: userData.lastName,
            email: userData.email,
            password: userData.password,
            locale: userData.locale,
        });
    }

    async getMemberInfo(memberId: string): Promise<Member> {
        const endpoint = `/members/${memberId}`;
        return this.axiosAuthentifiedRequest
            .get(endpoint)
            .then((response: AxiosResponse) => response.data);
    }
    async getMemberSites(memberId: string): Promise<Site[]> {
        const endpoint = `/members/${memberId}/sites`;
        const response = await this.axiosAuthentifiedRequest.get(endpoint);
        return response.data;
    }

    postRequestResetPassword(email: string): Promise<AxiosResponse> {
        const endpoint = '/auth/password/reset/request';
        return this.axiosRequest.post(endpoint, { email });
    }

    postConfirmPasswordReset(passwordInfos: {
        email: string;
        password: string;
    }): Promise<AxiosResponse> {
        const endpoint = '/auth/password/reset/confirm';
        return this.axiosRequest.post(endpoint, passwordInfos);
    }

    getUserTeams() {
        const endpoint = '/users/me/teams';
        return this.axiosAuthentifiedRequest
            .get(endpoint)
            .then((response: AxiosResponse) => response.data);
    }

    getClients(queryParams: object): Promise<AxiosResponse<GetClientsRes>> {
        const endpoint = '/clients';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    updateClientAccessGroups(
        clientId: string,
        bodyParams: { accessGroups: string[] },
    ): Promise<AxiosResponse> {
        const endpoint = `/clients/${clientId}/accessgroups`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    removeSite(siteId: string): Promise<AxiosResponse> {
        const endpoint = `/sites/${siteId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    transferSiteToAnotherClient(siteId: string, clientId: string): Promise<AxiosResponse> {
        const endpoint = `/sites/${siteId}/client/${clientId}/transfer`;
        return this.axiosAuthentifiedRequest.patch(endpoint);
    }

    updateSiteStatus(siteId: string, body: { status: boolean }): Promise<AxiosResponse> {
        const endpoint = `/sites/${siteId}/status`;
        return this.axiosAuthentifiedRequest.patch(endpoint, body);
    }

    async getClientInfo(clientId: string): Promise<responseGetClientInformation> {
        const endpoint = `/clients/${clientId}`;
        return await this.axiosAuthentifiedRequest.get(endpoint).then((response) => response.data);
    }

    getReportTypes(params: object = { pagination: false }): Promise<AxiosResponse<ReportType[]>> {
        const endpoint = '/reports/types';
        return this.axiosAuthentifiedRequest.get(endpoint, { params });
    }
    getSites(
        queryParams: QueryParams & { sortKey?: string; sortOrder?: string },
    ): Promise<AxiosResponse<SitesResponse>> {
        const endpoint = '/sites';
        const params = { ...queryParams };
        if (params && params.sortKey === 'siteName') {
            params.order = `name_${params.sortOrder?.toLowerCase()}`;
        }
        return this.axiosAuthentifiedRequest.get(endpoint, { params });
    }
    inviteMember(
        teamId: string,
        payload: InviteMemberPayload,
    ): Promise<AxiosResponse<InvitedMemberResponse>> {
        const endpoint = `/teams/${teamId}/members/invite`;
        return this.axiosAuthentifiedRequest.post<InvitedMemberResponse>(endpoint, payload);
    }

    postAcceptTeamInvitation(teamId: string, inviteCode: string): Promise<AxiosResponse> {
        const endpoint = `/teams/${teamId}/members/invite/accept?invitecode=${inviteCode}`;
        return this.axiosRequest.post(endpoint);
    }

    getMembers(teamId: string, params: GetMembersParams): Promise<MembersResponse> {
        const endpoint = `/teams/${teamId}/members`;
        return this.axiosAuthentifiedRequest
            .get(endpoint, { params })
            .then((response: AxiosResponse<MembersResponse>) => response.data);
    }

    async getMemberClients(
        memberId: string,
        params: { search?: string; page?: number; perpage?: number },
    ): Promise<ClientAccessResponse> {
        const endpoint = `/members/${memberId}/access`;
        const response = await this.axiosAuthentifiedRequest.get(endpoint, { params });
        return response.data;
    }
    async revokeSiteToMember(memberId: string, siteId: string): Promise<AxiosResponse> {
        const endpoint = `/members/${memberId}/sites/${siteId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }
    async associateMemberToSite(
        memberId: string,
        bodyParams: { siteId: string },
    ): Promise<AxiosResponse> {
        const endpoint = `/members/${memberId}/sites/`;
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    async getSentenceCategories(
        params: GetSentenceCategoriesParams,
    ): Promise<SentenceCategoriesResponse> {
        const adaptedParams: Record<string, string | number | boolean | undefined> = { ...params };
        if (adaptedParams.perpage === -1) {
            delete adaptedParams.perpage;
            adaptedParams.pagination = false;
        }
        const endpoint = '/automaticsentence/categories';
        const response = await this.axiosAuthentifiedRequest.get(endpoint, {
            params: adaptedParams,
        });
        return response.data;
    }

    addSentenceCategory(payload: AddSentenceCategoryPayload): Promise<AxiosResponse> {
        const endpoint = '/automaticsentence/categories';
        return this.axiosAuthentifiedRequest.post(endpoint, payload);
    }

    updateSentenceCategory(
        categoryId: string,
        payload: UpdateSentenceCategoryPayload,
    ): Promise<AxiosResponse> {
        const endpoint = `/automaticsentence/categories/${categoryId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, payload);
    }

    removeSentenceCategory(categoryId: string): Promise<AxiosResponse> {
        const endpoint = `/automaticsentence/categories/${categoryId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    getAutomaticSentences(
        params: GetAutomaticSentencesParams,
    ): Promise<AutomaticSentencesResponse> {
        const endpoint = '/automaticsentence/sentences';
        return this.axiosAuthentifiedRequest
            .get(endpoint, { params })
            .then((response: AxiosResponse<AutomaticSentencesResponse>) => response.data);
    }

    addAutomaticSentence(payload: AutomaticSentencePayload): Promise<AxiosResponse> {
        const endpoint = '/automaticsentence/sentences';
        return this.axiosAuthentifiedRequest.post(endpoint, payload);
    }

    updateAutomaticSentence(
        sentenceId: string,
        payload: AutomaticSentencePayload,
    ): Promise<AxiosResponse> {
        const endpoint = `/automaticsentence/sentences/${sentenceId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, payload);
    }

    updateSentenceStatus(sentenceId: string, status: boolean): Promise<AxiosResponse> {
        const endpoint = `/automaticsentence/sentences/${sentenceId}/status`;
        return this.axiosAuthentifiedRequest.patch(endpoint, { status });
    }

    removeAutomaticSentence(sentenceId: string): Promise<AxiosResponse> {
        const endpoint = `/automaticsentence/sentences/${sentenceId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    getAccessGroups(
        queryParams?: Record<string, string | number | boolean>,
    ): Promise<AccessGroup[]> {
        const endpoint = '/accessgroups';
        return this.axiosAuthentifiedRequest
            .get(endpoint, { params: queryParams })
            .then((response) => {
                if (response.data.pagination) {
                    return {
                        accessGroups: response.data.accessGroups || [],
                        pagination: response.data.pagination,
                    };
                }
                return response.data.accessGroups || response.data || [];
            });
    }
    getAccessGroup(accessGroupId: string, queryParams: QueryParams): Promise<AccessGroupDetail> {
        const endpoint = `/accessgroups/${accessGroupId}`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    addAccessGroup(bodyParams: object): Promise<AxiosResponse> {
        const endpoint = '/accessgroups';
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    deleteAccessGroup(accessGroupId: string): Promise<AxiosResponse> {
        const endpoint = `/accessgroups/${accessGroupId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    updateAccessGroup(accessGroupId: string, bodyParams: object): Promise<AxiosResponse> {
        const endpoint = `/accessgroups/${accessGroupId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    getContactFunctions(
        queryParams?: Record<string, string | number | boolean>,
    ): Promise<AxiosResponse<ContactFunctionListResponse>> {
        const endpoint = '/contactFunctions';
        return this.axiosAuthentifiedRequest.get(endpoint, { params: queryParams });
    }

    getContactFunction(contactFunctionId: string): Promise<AxiosResponse<ContactFunctionDetail>> {
        const endpoint = `/contactFunctions/${contactFunctionId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    addContactFunction(bodyParams: object): Promise<AxiosResponse> {
        const endpoint = '/contactFunctions';
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    updateContactFunction(contactFunctionId: string, bodyParams: object): Promise<AxiosResponse> {
        const endpoint = `/contactFunctions/${contactFunctionId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    deleteContactFunction(contactFunctionId: string): Promise<AxiosResponse> {
        const endpoint = `/contactFunctions/${contactFunctionId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    updateCanAccessToAllClients(memberId: string, bodyParams: { canAccessAllClients: boolean }) {
        const endPoint = `/members/${memberId}/accessallclients`;
        return this.axiosAuthentifiedRequest.patch(endPoint, bodyParams);
    }

    resendMemberInvitation(teamId: string, memberId: string): Promise<AxiosResponse<Member>> {
        const endpoint = `/teams/${teamId}/members/${memberId}/invite/resend`;
        return this.axiosAuthentifiedRequest.post(endpoint);
    }

    async updateMember(
        teamId: string,
        memberId: string,
        payload: UpdateMemberPayload,
    ): Promise<Member> {
        const endpoint = `/teams/${teamId}/members/${memberId}`;
        return this.axiosAuthentifiedRequest
            .patch(endpoint, payload)
            .then((response: AxiosResponse<Member>) => response.data);
    }

    async updateMemberDetails(memberId: string, payload: UpdateMemberPayload): Promise<Member> {
        const endpoint = `/members/${memberId}`;
        return this.axiosAuthentifiedRequest
            .patch(endpoint, payload)
            .then((response: AxiosResponse<Member>) => response.data);
    }
    updateMemberRole(memberId: string, roles: UpdateMemberPayload): Promise<Member> {
        const endpoint = `/members/${memberId}`;
        return this.axiosAuthentifiedRequest.put(endpoint, roles);
    }

    async updateMembersAIActivation(
        teamId: string,
        payload: UpdateMembersAIActivationPayload,
    ): Promise<AxiosResponse> {
        const endpoint = `/teams/${teamId}/members/ai-activation`;
        return this.axiosAuthentifiedRequest.patch(endpoint, payload);
    }

    updateProfile(userId: string, payload: ProfilePayload): Promise<Member> {
        const endpoint = `/users/${userId}/profile`;
        return this.axiosAuthentifiedRequest.put(endpoint, payload);
    }

    updateProfileDetails(payload: {
        profile: {
            firstName: string;
            lastName: string;
            phoneNumber: object;
        };
        preferences: {
            locale: string;
        };
    }): Promise<AxiosResponse> {
        const endpoint = '/users/me/profile';
        return this.axiosAuthentifiedRequest.put(endpoint, payload);
    }

    getProfileDetails(): Promise<ProfileDetails> {
        const endpoint = '/users/me/profile';
        return this.axiosAuthentifiedRequest
            .get(endpoint)
            .then((response: AxiosResponse) => response.data);
    }

    deleteMember(memberId: string): Promise<AxiosResponse> {
        const endpoint = `/members/${memberId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    addClientAccess(memberId: string, payload: AddClientAccessPayload): Promise<AxiosResponse> {
        const endpoint = `/members/${memberId}/clients`;
        return this.axiosAuthentifiedRequest.post(endpoint, payload);
    }

    removeClientAccess(memberId: string, clientId: string): Promise<AxiosResponse> {
        const endpoint = `/members/${memberId}/clients/${clientId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    getMemberAccessGroups(memberId: string): Promise<AccessGroup[]> {
        const endpoint = `/members/${memberId}/accessgroups`;
        return this.axiosAuthentifiedRequest
            .get(endpoint)
            .then((response) => response.data.accessGroups || []);
    }

    updateMemberAccessGroup(
        memberId: string,
        payload: UpdateMemberAccessGroupPayload,
    ): Promise<AxiosResponse> {
        const endpoint = `/members/${memberId}/accessgroups`;
        return this.axiosAuthentifiedRequest.patch(endpoint, payload);
    }

    getTimezone(lat: number, lng: number) {
        const endpoint = '/world/timezone';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: { lat, lng },
        });
    }

    public async getStandardFamilies(
        params?: GetStandardFamiliesParams,
    ): Promise<AxiosResponse<GetStandardFamiliesRes>> {
        const endpoint = '/standardsfamilies';
        const requestParams = params ?? { pagination: false };
        return this.axiosAuthentifiedRequest.get(endpoint, { params: requestParams });
    }

    addStandardFamily(payload: StandardFamilyPayload): Promise<AxiosResponse<StandardFamily>> {
        const endpoint = '/standardsfamilies';
        return this.axiosAuthentifiedRequest.post(endpoint, payload);
    }

    updateStandardFamily(
        standardFamilyId: string,
        payload: StandardFamilyPayload,
    ): Promise<AxiosResponse<StandardFamily>> {
        const endpoint = `/standardsfamilies/${standardFamilyId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, payload);
    }

    deleteStandardFamily(standardFamilyId: string): Promise<AxiosResponse> {
        const endpoint = `/standardsfamilies/${standardFamilyId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    getStandards(params: GetStandardsParams): Promise<AxiosResponse<GetStandardsRes>> {
        const endpoint = '/standards';
        return this.axiosAuthentifiedRequest.get(endpoint, { params });
    }

    addStandard(payload: StandardPayload): Promise<AxiosResponse<StandardDeepCopy>> {
        const endpoint = '/standards';
        return this.axiosAuthentifiedRequest.post(endpoint, payload);
    }

    updateStandard(
        standardId: string,
        payload: StandardPayload,
    ): Promise<AxiosResponse<StandardDeepCopy>> {
        const endpoint = `/standards/${standardId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, payload);
    }

    deleteStandard(standardId: string): Promise<AxiosResponse> {
        const endpoint = `/standards/${standardId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    public async getCoupons(params?: GetCouponsParams): Promise<AxiosResponse<CouponsResponse>> {
        return this.axiosAuthentifiedRequest.get('/lab/coupons', { params });
    }

    addClient(bodyParams: object): Promise<AxiosResponse<Client>> {
        const endpoint = '/clients';
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }
    getComponentConfigurations(
        componentId: string,
        bodyParams?: QueryParams,
    ): Promise<AxiosResponse<{ configurations: ComponentConfiguration[] }>> {
        const endpoint = `/components/${componentId}/configurations`;
        return this.axiosAuthentifiedRequest.get(endpoint, { params: bodyParams });
    }

    getComponentLabAnalysis(
        componentId: string,
        queryParams: QueryParams,
    ): Promise<AxiosResponse<LabAnalysisTypeConfiguration[]>> {
        const endpoint = `/components/${componentId}/labAnalysisTypes`;

        return this.axiosAuthentifiedRequest.get<LabAnalysisTypeConfiguration[]>(endpoint, {
            params: queryParams,
        });
    }

    removeClient(clientId: string | undefined) {
        const endpoint = `/clients/${clientId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    activateOrDeactivateClient(clientId: string | undefined, status: boolean) {
        const endpoint = `/clients/${clientId}/status`;
        return this.axiosAuthentifiedRequest.patch(endpoint, { status });
    }

    getClientSites(clientId: string, queryParams: object): Promise<AxiosResponse<SitesResponse>> {
        const endpoint = `/clients/${clientId}/sites`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }
    deleteClientSite(clientId: string) {
        const endpoint = `/sites/${clientId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    addClientSite(clientId: string, siteData: object): Promise<AxiosResponse<Site>> {
        const endpoint = `/clients/${clientId}/sites`;
        return this.axiosAuthentifiedRequest.post(endpoint, siteData);
    }
    getMemberAccess(
        memberId: string,
        queryParams: object,
    ): Promise<AxiosResponse<GetMemberAccessRes>> {
        const endpoint = `/members/${memberId}/access`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }
    activateOrDeactivateClientSite(siteId: string, status: boolean) {
        const endpoint = `/sites/${siteId}/status`;
        return this.axiosAuthentifiedRequest.patch(endpoint, { status });
    }

    sendFeedBack(bodyParams: object) {
        const endpoint = `/feedback`;
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    changePassword(payload: { oldPassword: string; password: string }): Promise<AxiosResponse> {
        const endpoint = '/auth/password/change';
        return this.axiosAuthentifiedRequest.post(endpoint, payload);
    }

    updateSignature(payload: { en: string; fr: string }): Promise<AxiosResponse> {
        const endpoint = '/members/me/signature';
        return this.axiosAuthentifiedRequest.patch(endpoint, payload);
    }

    getMassBalanceAnalysis(
        teamId: string,
        type: string,
    ): Promise<AxiosResponse<MassBalanceAnalysis[]>> {
        const endpoint = '/analyses/types/massbalance';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: {
                team: teamId,
                type,
            },
        });
    }
    editOneMassBalanceConfig(siteId: string, massBalanceId: string, params: MassBalancePayload) {
        const endpoint = `sites/${siteId}/massBalance/${massBalanceId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, params);
    }
    deleteOneMassBalanceConfig(siteId: string, massBalanceId: string) {
        const endpoint = `sites/${siteId}/massBalance/${massBalanceId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    async uploadSignatureFile(file: File): Promise<AxiosResponse> {
        const formData = new FormData();
        formData.append('file', file);
        return this.axiosAuthentifiedRequest.post('/members/me/signature/file', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    }
    async deleteSignature(): Promise<AxiosResponse> {
        const endpoint = '/members/me/signature/file';
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }
    async deleteFile(fileId: string) {
        const endpoint = `/files/${fileId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    async deleteAnalysisFile(
        workOrderId: string,
        activityId: string,
        analysisId: string,
        fileId: string,
    ) {
        const endpoint = `/workOrders/${workOrderId}/activity/${activityId}/analysis/${analysisId}/files/${fileId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    deleteComment(workOrderId: string, commentId: string) {
        const endpoint = `/workorders/${workOrderId}/comments/${commentId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    addMeasureToAnalysis(workOrderId: string, activityId: string, payload: object) {
        const endpoint = `/workorders/${workOrderId}/activity/${activityId}/analysis`;
        return this.axiosAuthentifiedRequest.patch(endpoint, payload);
    }

    fetchLastMeasure(params: object): Promise<AxiosResponse<LastReading>> {
        const endpoint = 'sites/lastReading';
        return this.axiosAuthentifiedRequest.patch(endpoint, params);
    }
    getDevicesByNetwork(activityId: string): Promise<AxiosResponse<Devices>> {
        const endpoint = `/activities/${activityId}/summary/devices`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }
    getTables(activityId: string): Promise<AxiosResponse<{ tables: Table[] }>> {
        const endpoint = `/activities/${activityId}/summary/tables`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    updateTechfile(siteId: string, nodeId: string, payload: object): Promise<AxiosResponse> {
        const endpoint = `/sites/${siteId}/techfiles/${nodeId}`;
        return this.axiosAuthentifiedRequest.put(endpoint, payload, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    }

    getMeasureHistory(queryParams: object): Promise<AxiosResponse<MeasureHistory>> {
        const endpoint = '/analyses/measures/history/latest';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    updateManualColorNorm(bodyParams: { analysisId: string; [key: string]: string }) {
        const endpoint = `workOrders/analysis/${bodyParams.analysisId}/manualColorNorm`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    getNodeInfo<T>(
        siteId: string,
        nodeId: string,
        queryParams?: object,
    ): Promise<AxiosResponse<T>> {
        const endpoint = `/sites/${siteId}/techfiles/${nodeId}`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    postCalculatedMassBalance(workOrderId: string) {
        const endpoint = `/workorders/${workOrderId}/calculateMassBalance`;
        return this.axiosAuthentifiedRequest.post(endpoint);
    }

    toggleShowAllPdfInMassBalanceCategory(workOrderId: string, showAllMassBalanceOnReport: object) {
        const endpoint = `/workorders/${workOrderId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, { showAllMassBalanceOnReport });
    }

    getCalculatedMassBalance(workOrderId: string): Promise<AxiosResponse> {
        const endpoint = `/workorders/${workOrderId}/getMassBalances`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    getMassBalanceInfos(workOrderId: string, massBalanceId: string): Promise<AxiosResponse> {
        const endpoint = `workorders/${workOrderId}/massBalance/${massBalanceId}/infos`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    updateMassBalanceInfos(workOrderId: string, massBalanceId: string, params: object) {
        const endpoint = `workorders/${workOrderId}/massBalance/${massBalanceId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, params);
    }

    toggleshowPdfInMeasureMassBalance(workOrderId: string, massBalanceId: string, params: object) {
        const endpoint = `workorders/${workOrderId}/massBalance/${massBalanceId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, params);
    }

    updateMeasureNote(
        activityId: string,
        tableId: string,
        measureId: string,
        bodyParams: object,
    ): Promise<AxiosResponse<MeasureNoteResponse>> {
        const endpoint = `/activities/${activityId}/summary/tables/${tableId}/measures/${measureId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    updateWorkOrderActivityAnalysisMeasure(
        workOrderId: string,
        activityId: string,
        bodyParams: object,
    ): Promise<AxiosResponse> {
        const endpoint = `/workorders/${workOrderId}/activity/${activityId}/analysis`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    async getNetworkAnalysis(
        activityId: string,
        networkId: string,
    ): Promise<AxiosResponse<NetworkAnalysisData>> {
        let inFlight = this.networkAnalysisInFlight.get(activityId);
        if (!inFlight) {
            inFlight = this.axiosAuthentifiedRequest
                .get<NetworkAnalysisBulkEntry[]>(`activities/${activityId}/analyses`)
                .then((res) => {
                    const map = new Map<string, NetworkAnalysisData>();
                    const entries = Array.isArray(res.data) ? res.data : [];
                    for (const entry of entries) {
                        if (!entry?.networkRef) continue;
                        map.set(entry.networkRef.toString(), {
                            analysisTypes: entry.analysisTypes,
                            components: entry.components,
                        });
                    }
                    return map;
                })
                .finally(() => {
                    this.networkAnalysisInFlight.delete(activityId);
                });
            this.networkAnalysisInFlight.set(activityId, inFlight);
        }
        const map = await inFlight;
        const data = map.get(networkId) || { analysisTypes: [], components: [] };
        return {
            data,
            status: 200,
            statusText: 'OK',
            headers: {},
            config: {},
        } as AxiosResponse<NetworkAnalysisData>;
    }

    updateComponentNote(
        activityId: string,
        tableId: string,
        componentId: string,
        bodyParams: object,
    ): Promise<AxiosResponse> {
        const endpoint = `/activities/${activityId}/summary/tables/${tableId}/components/${componentId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    removeAnalysisRequest(analysisRequestId: string) {
        const endpoint = `/analysisRequests/${analysisRequestId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    getAnalysisMeasure(
        workOrderId: string,
        activityId: string,
        analysisId: string,
    ): Promise<AxiosResponse<Analysis>> {
        const endpoint = `/workorders/${workOrderId}/activity/${activityId}/analysis/${analysisId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    getInventoryActivity(
        workOrderId: string,
        group = 'product',
    ): Promise<AxiosResponse<{ id: string; workOrder: string; items: ProductInventory }>> {
        const endpoint = `workorders/${workOrderId}/inventory`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: {
                group,
            },
        });
    }

    updateInventoryActivityItem(workOrderId: string, bodyParams: object) {
        const endpoint = `workorders/${workOrderId}/inventory`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    async getFileBlob(fileId: string): Promise<Blob> {
        const response = await this.axiosAuthentifiedRequest.get(`/files/${fileId}`, {
            responseType: 'blob',
        });
        return response.data as Blob;
    }

    async getHtmlReport(htmlId: string): Promise<string> {
        const response = await this.axiosAuthentifiedRequest.get(`/html-reports/${htmlId}`, {
            responseType: 'text',
        });
        return response.data as string;
    }
    getAllSiteNetwork(siteId: string, type: string): Promise<Network[]> {
        const endpoint = `/sites/${siteId}/networkByType`;
        return this.axiosAuthentifiedRequest
            .get(endpoint, { params: { type } })
            .then((response: AxiosResponse) => response.data);
    }
    getAllNetworks(siteId: string): Promise<AxiosResponse<Network[]>> {
        const endpoint = `/sites/${siteId}/allnetworks`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }
    getMassBalanceFlowMeterByNetwork(
        siteId: string,
        networkId: string,
        type: string,
    ): Promise<AxiosResponse<Array<{ id: string; name: string }>>> {
        const endpoint = `/sites/${siteId}/devices/flowMetersByNetwork`;
        return this.axiosAuthentifiedRequest.get(endpoint, { params: { networkId, type } });
    }
    getAllMassBalanceFlowMeter(siteId: string): Promise<AxiosResponse<MassBalanceFlowMeterData>> {
        const endpoint = `/sites/${siteId}/e4massBalance/flowmeters`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }
    getMassBalanceAnalysisByComponents(
        networkId: string,
        analysisTypeId: string,
    ): Promise<AxiosResponse<MassBalanceAnalysis[]>> {
        const endpoint = '/analyses/types/massbalance/components';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: { networkId, 'analysisTypeIds[]': analysisTypeId },
        });
    }

    addMassBalanceBoiler(siteId: string, massBalanceId: string, bodyParams: object) {
        const endpoint = `/sites/${siteId}/massBalance/${massBalanceId}/e4boiler`;
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    addMassBalanceCoolingTower(siteId: string, massBalanceId: string, bodyParams: object) {
        const endpoint = `/sites/${siteId}/massBalance/${massBalanceId}/e4cooling`;
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    deleteMassBalanceBoiler(siteId: string, massBalanceId: string) {
        const endpoint = `/sites/${siteId}/massBalance/${massBalanceId}/e4boiler`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    deleteMassBalanceCoolingTower(siteId: string, massBalanceId: string) {
        const endpoint = `/sites/${siteId}/massBalance/${massBalanceId}/e4cooling`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    getMassBalanceCoolingTower(
        siteId: string,
        massBalanceId: string,
    ): Promise<AxiosResponse<E4CoolingApiResponse>> {
        const endpoint = `/sites/${siteId}/massBalance/${massBalanceId}/e4cooling`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    getMassBalanceBoiler(
        siteId: string,
        massBalanceId: string,
    ): Promise<AxiosResponse<E4BoilerApiResponse>> {
        const endpoint = `/sites/${siteId}/massBalance/${massBalanceId}/e4boiler`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    getOneMassBalanceConfig(
        siteId: string,
        massBalanceId: string,
        params?: { populateFlowMeters?: boolean },
    ): Promise<AxiosResponse<MassBalanceConfig>> {
        const endpoint = `/sites/${siteId}/massBalance/${massBalanceId}`;
        return this.axiosAuthentifiedRequest.get(endpoint, { params });
    }
    getMassBalanceProductByNetwork(
        siteId: string,
        networkId: string,
    ): Promise<AxiosResponse<SiteProduct[]>> {
        const endpoint = `/sites/${siteId}/devices/productsByNetwork`;
        return this.axiosAuthentifiedRequest.get(endpoint, { params: { networkId } });
    }
    getMassBalanceProductByComponent(
        siteId: string,
        networkId: string,
        siteProductId: string,
    ): Promise<AxiosResponse<SiteProduct[]>> {
        const endpoint = `/sites/${siteId}/devices/componentsByProduct`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: { productId: siteProductId, networkId },
        });
    }
    // Get components by network (used in E4 settings when selecting energy mix per network)
    getComponentsByNetwork(
        siteId: string,
        networkId: string,
    ): Promise<AxiosResponse<Array<{ id: string; name: string }>>> {
        const endpoint = `/sites/${siteId}/network/${networkId}/components`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    getAllComponentsByNetwork(
        siteId: string,
        networkId: string,
    ): Promise<AxiosResponse<Array<{ id: string; name: string; active?: boolean }>>> {
        const endpoint = `/sites/${siteId}/network/${networkId}/allComponentsByNetwork`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    addAllMassBalanceConfig(siteId: string, params: MassBalancePayload) {
        const endpoint = `sites/${siteId}/massBalance`;
        return this.axiosAuthentifiedRequest.post(endpoint, params);
    }
    getAllMassBalanceConfig(siteId: string): Promise<AxiosResponse<MassBalanceConfig[]>> {
        const endpoint = `sites/${siteId}/massBalance`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }
    async updateClientInfo(clientId: string, clientInfo: payloadClientInformation) {
        const endpoint = `/clients/${clientId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, clientInfo);
    }
    getTreatmentUnits(lang: string): Promise<AxiosResponse> {
        const endpoint = `/networks/units/treatments?lang=${lang}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    getTreatmentProgramById(
        networkId: string,
        treatmentId: string,
    ): Promise<AxiosResponse<TreatmentProgramInterface>> {
        const endpoint = `/networks/${networkId}/treatments/${treatmentId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    updateTreatmentProgram(
        networkId: string,
        treatmentId: string,
        payload: object,
    ): Promise<AxiosResponse> {
        const endpoint = `/networks/${networkId}/treatments/${treatmentId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, payload);
    }

    getFrequencies(queryParams?: object): Promise<AxiosResponse<GetFrequenciesRes>> {
        const endpoint = '/maintenanceschedule/frequencies';
        return this.axiosAuthentifiedRequest.get(endpoint, { params: queryParams });
    }

    addFrequency(bodyParams: object): Promise<AxiosResponse> {
        const endpoint = '/maintenanceschedule/frequencies';
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    updateFrequency(frequencyId: string, bodyParams: object): Promise<AxiosResponse> {
        const endpoint = `/maintenanceschedule/frequencies/${frequencyId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    removeFrequency(frequencyId: string): Promise<AxiosResponse> {
        const endpoint = `/maintenanceschedule/frequencies/${frequencyId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    getActivities(queryParams: object): Promise<AxiosResponse<GetActivitiesRes>> {
        const endpoint = '/maintenanceschedule/activities';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    addActivity(bodyParams: object): Promise<AxiosResponse> {
        const endpoint = '/maintenanceschedule/activities';
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    updateActivity(activityId: string, bodyParams: object): Promise<AxiosResponse> {
        const endpoint = `/maintenanceschedule/activities/${activityId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    removeActivity(activityId: object): Promise<AxiosResponse> {
        const endpoint = `/maintenanceschedule/activities/${activityId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    updateActivitiesPosition(activityId: string, bodyParams: object): Promise<AxiosResponse> {
        const endpoint = `/maintenanceschedule/activities/${activityId}/reorder`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    getScheduleTemplate(queryParams: object): Promise<AxiosResponse<ScheduleTemplateRes>> {
        const endpoint = '/maintenanceschedule/template';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    getSchedules(queryParams: object): Promise<AxiosResponse<ScheduleData[]>> {
        const endpoint = '/maintenanceschedule/schedules';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    getSchedule(scheduleId: string): Promise<AxiosResponse<ScheduleData>> {
        const endpoint = `/maintenanceschedule/schedules/${scheduleId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    addSchedule(bodyParams: SchedulePayload): Promise<AxiosResponse> {
        const endpoint = '/maintenanceschedule/schedules';
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    updateSchedule(scheduleId: string, bodyParams: SchedulePayload): Promise<AxiosResponse> {
        const endpoint = `/maintenanceschedule/schedules/${scheduleId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    getMaintenanceScheduleCategories(
        queryParams: object,
    ): Promise<AxiosResponse<ScheduleCategoriesRes>> {
        const endpoint = '/maintenanceschedule/categories';
        return this.axiosAuthentifiedRequest.get(endpoint, { params: queryParams });
    }

    addMaintenanceScheduleCategory(bodyParams: object): Promise<AxiosResponse> {
        const endpoint = '/maintenanceschedule/categories';
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    updateMaintenanceScheduleCategory(
        categoryId: string,
        bodyParams: object,
    ): Promise<AxiosResponse> {
        const endpoint = `/maintenanceschedule/categories/${categoryId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    removeMaintenanceScheduleCategory(categoryId: string): Promise<AxiosResponse> {
        const endpoint = `/maintenanceschedule/categories/${categoryId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }
    getCoolingNetworkReport(
        networkId: string,
        queryParams: { componentId: string },
    ): Promise<AxiosResponse<Blob>> {
        const endpoint = `/networks/${networkId}/report/cooling`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            responseType: 'blob',
            params: queryParams,
        });
    }

    addTreatmentProgram(networkId: string, payload: object): Promise<AxiosResponse> {
        const endpoint = `/networks/${networkId}/treatments`;
        return this.axiosAuthentifiedRequest.post(endpoint, payload);
    }

    getUnits(siteId: string, lang: string): Promise<AxiosResponse> {
        const endpoint = `/sites/${siteId}/techfiles/units?lang=${lang}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    getSiteInfo(siteId: string): Promise<AxiosResponse> {
        const endpoint = `/sites/${siteId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }
    updateSite(siteId: string, payload: object): Promise<AxiosResponse> {
        const endpoint = `/sites/${siteId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, payload);
    }
    getNodeTree(
        nodeId: string,
        nodeType: string,
        queryParams: object,
    ): Promise<AxiosResponse<NodeTreeResponse>> {
        const endpoint = `${getNodeType(nodeType)}/${nodeId}/nodes/tree`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }
    addWorkOrder(bodyParams: object): Promise<AxiosResponse<WorkOrder>> {
        const endpoint = '/workorders';
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }
    addNode(parentNodeId: string, parentNodeType: string, params: { active?: boolean }) {
        const endpoint = `${getNodeType(parentNodeType)}/${parentNodeId}/nodes`;
        const bodyParams = cloneDeep(params);

        delete bodyParams.active;

        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }
    editNode(nodeInfo: { nodeTypeToRedirect: string; id: string }) {
        const endpoint = `${getNodeType(nodeInfo.nodeTypeToRedirect)}/${nodeInfo.id}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, nodeInfo);
    }
    updateComponentsForAnalysisFormula(
        siteId: string,
        configurationId: string,
        payload: { formula?: string | null; variables: Partial<ComponentConfiguration>[] },
    ): Promise<AxiosResponse> {
        const endpoint = `/analyses/analysisconfigurations/${configurationId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, payload);
    }
    addChartAction(bodyParams: object): Promise<AxiosResponse> {
        const endpoint = '/chartActions';
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    getChartsAction(queryParams: object): Promise<AxiosResponse<ListResponse<ChartAction[]>>> {
        const endpoint = '/chartActions';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }
    getControlChart(componentId: string): Promise<AxiosResponse<GetControlChart>> {
        const endpoint = `/components/${componentId}/controlChart/`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }
    updateControlChart(componentId: string, payload: ControlChartPayload): Promise<AxiosResponse> {
        const endpoint = `/components/${componentId}/controlChart/`;
        return this.axiosAuthentifiedRequest.patch(endpoint, payload);
    }

    deleteComponentConfiguration(componentId: string, configurationId: string) {
        const endpoint = `/components/${componentId}/configurations/${configurationId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }
    addComponentConfiguration(componentId: string, bodyParams: object) {
        const endpoint = `/components/${componentId}/configurations`;
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }
    getAnalysisTypes(
        queryParams?: Record<string, string | number | boolean | string[]>,
    ): Promise<AxiosResponse<ListResponse<GetAnalysisTypesResponse>>> {
        const paramsWithDefaults = queryParams ?? { pagination: false };
        const endpoint = '/analyses/types';
        return this.axiosAuthentifiedRequest.get(endpoint, { params: paramsWithDefaults });
    }

    getAnalysisType(typeId: string): Promise<AxiosResponse<AnalysisType>> {
        const endpoint = `/analyses/types/${typeId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    getDeviceAnalysisKeys(
        params: { pagination?: boolean } = { pagination: false },
    ): Promise<AxiosResponse<DeviceAnalysisKeysResponse>> {
        const endpoint = '/deviceAnalysisKeys';
        return this.axiosAuthentifiedRequest.get(endpoint, { params });
    }

    createAnalysisType(payload: AnalysisTypePayload): Promise<AxiosResponse<AnalysisType>> {
        const endpoint = '/analyses/types';
        return this.axiosAuthentifiedRequest.post(endpoint, payload);
    }

    updateAnalysisType(
        typeId: string,
        payload: AnalysisTypePayload,
    ): Promise<AxiosResponse<AnalysisType>> {
        const endpoint = `/analyses/types/${typeId}`;
        return this.axiosAuthentifiedRequest.put(endpoint, payload);
    }

    addWorkOrderManyAnalysisOnComponent(
        workOrderId: string,
        activityId: string,
        bodyParams: object,
    ): Promise<AxiosResponse> {
        const endpoint = `/workorders/${workOrderId}/activity/${activityId}/analysisOnComponent`;
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    getAnalysisGroups(
        queryParams?: Record<string, string | number | boolean>,
    ): Promise<AxiosResponse<AnalysisGroupsResponse>> {
        const endpoint = '/analyses/groups';
        return this.axiosAuthentifiedRequest.get(endpoint, { params: queryParams });
    }
    getComponentsForAnalysisType(
        siteId: string,
        analysisTypeId: string,
        componentId: string,
    ): Promise<AxiosResponse<GetComponentsForAnalysisType>> {
        const endpoint = `/sites/${siteId}/analysis/${analysisTypeId}/components/${componentId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }
    getARTemplateByComponent(
        siteId: string,
        componentId: string,
    ): Promise<AxiosResponse<ARTemplateByComponentResponse>> {
        const endpoint = `/sites/${siteId}/component/${componentId}/template`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }
    getTreatmentName(
        siteId: string,
        componentId: string,
        queryParams?: Record<string, string | number | boolean>,
    ): Promise<AxiosResponse<TreatmentResponse>> {
        const endpoint = `/sites/${siteId}/component/${componentId}/treatement`;
        return this.axiosAuthentifiedRequest.get(endpoint, { params: queryParams });
    }
    updateComponentsForAnalysisType(
        siteId: string,
        analysisId: string,
        payload: { formula: string; variables: Partial<ComponentConfiguration>[] },
    ): Promise<AxiosResponse> {
        const endpoint = `/sites/${siteId}/analysis/${analysisId}`;
        return this.axiosAuthentifiedRequest.put(endpoint, payload);
    }
    getNode(nodeId: string, nodeType: string) {
        const endpoint = `${getNodeType(nodeType)}/${nodeId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }
    addFile(params: object | null, file: FormData) {
        const endpoint = '/files';
        return this.axiosAuthentifiedRequest.post(endpoint, file, {
            params,
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    }
    getFile(fileId: string) {
        const endpoint = `/files/${fileId}/metadata`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }
    downloadFile(fileId: string) {
        const endpoint = `/files/${fileId}`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            responseType: 'blob',
        });
    }

    getS3File(config: object): Promise<AxiosResponse> {
        const endpoint = '/files/s3';
        return this.axiosAuthentifiedRequest.get(endpoint, config);
    }
    /**
     * @param {String} fileId
     * @return {Promise<string>} Returns a blob URL string
     */
    async getFileUrl(fileId: string): Promise<string> {
        const { data } = await this.axiosAuthentifiedRequest.get(`/files/${fileId}`, {
            responseType: 'blob',
        });

        return URL.createObjectURL(data);
    }
    addLabWorkOrderComment(labWorkOrderId: string, bodyParams: object) {
        const endpoint = `/lab/workorders/${labWorkOrderId}/comments`;
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }
    addComment(workOrderId: string, bodyParams: object) {
        const endpoint = `/workorders/${workOrderId}/comments`;
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }
    updateLabWorkOrderComment(labWorkOrderId: string, commentId: string, bodyParams: object) {
        const endpoint = `/lab/workorders/${labWorkOrderId}/comments/${commentId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }
    editComment(
        workOrderId: string,
        commentId: string,
        bodyParams: object,
    ): Promise<AxiosResponse> {
        const endpoint = `/workorders/${workOrderId}/comments/${commentId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }
    removeFilesToALabComment(commentId: string, labWorkOrderId: string, fileId: string) {
        const endpoint = `/lab/workorders/${labWorkOrderId}/comments/${commentId}/files/${fileId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }
    removeFilesToAComment(commentId: string, workOrderId: string, fileId: string) {
        const endpoint = `/workorders/${workOrderId}/comments/${commentId}/files/${fileId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }
    getMeasureHistoryAll(queryParams: {
        analysisType: string;
        component: string;
        page?: number;
        activity?: string;
        perpage?: number;
        type?: string;
        search?: string;
    }): Promise<AxiosResponse<MeasureHistoryResponse>> {
        const endpoint = '/analyses/measures/history/all';
        return this.axiosAuthentifiedRequest.get(endpoint, { params: queryParams });
    }
    getNetworkTypes(queryParams: object) {
        const endPoint = '/networktypes';
        return this.axiosAuthentifiedRequest.get(endPoint, {
            params: queryParams,
        });
    }
    getContainerTypes(queryParams: object) {
        const endPoint = '/containertypes';
        return this.axiosAuthentifiedRequest.get(endPoint, {
            params: queryParams,
        });
    }
    getComponentTypes(
        queryParams?: Record<string, string | number | boolean>,
    ): Promise<AxiosResponse<GetComponentTypesResponse>> {
        const paramsWithDefaults = queryParams ?? { pagination: false };
        const endPoint = '/componenttypes';
        return this.axiosAuthentifiedRequest.get(endPoint, {
            params: paramsWithDefaults,
        });
    }
    exportSiteAnalysisCSV(siteId: string, queryParams: object) {
        const endpoint = `/sites/${siteId}/exportAnalysisCSV`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
            responseType: 'blob',
        });
    }
    getSiteTree(siteId: string, queryParams?: object): Promise<AxiosResponse<NodeTreeResponse>> {
        const endpoint = `/sites/${siteId}/nodes/tree`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }
    getWorkOrderTypes(params = { pagination: false }) {
        const endpoint = '/workorders/types';
        return this.axiosAuthentifiedRequest.get(endpoint, { params });
    }
    getNetworkBySite(siteId: string): Promise<AxiosResponse<NetworkWithAnalyses[]>> {
        const endpoint = `/sites/${siteId}/networksWithAnalyses`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }
    editWOTemplate(woTemplateId: string, content: object) {
        const endpoint = `/workorders/templates/${woTemplateId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, content);
    }
    addWOTemplate(content: object) {
        const endpoint = 'workorders/templates';
        return this.axiosAuthentifiedRequest.post(endpoint, content);
    }
    reopenWorkOrder(workOrderId: string) {
        const endpoint = `/workorders/${workOrderId}/reopen`;
        return this.axiosAuthentifiedRequest.patch(endpoint);
    }

    getAnalysisRequests(queryParams: object): Promise<AxiosResponse<AnalysisRequestsResponse>> {
        const endpoint = '/analysisRequests';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    getContractsForTeam(params: {
        teamId: string;
        siteId: string;
    }): Promise<AxiosResponse<ContractsResponse>> {
        const endpoint = '/contracts';
        return this.axiosAuthentifiedRequest.get(endpoint, { params });
    }

    updateAnalysisRequest(analysisRequestId: string, payload: object): Promise<AxiosResponse> {
        const endpoint = `/analysisRequests/${analysisRequestId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, payload);
    }

    getAnalysisRequest(analysisRequestId: string): Promise<AxiosResponse<AnalysisRequest>> {
        const endpoint = `/analysisRequests/${analysisRequestId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    sendSampleEmail(analysisRequestId: string, sampleId: string): Promise<AxiosResponse> {
        const endpoint = `/analysisRequests/${analysisRequestId}/samples/${sampleId}/rejected`;
        return this.axiosAuthentifiedRequest.post(endpoint);
    }
    addLabWorkOrder(payload: object): Promise<AxiosResponse> {
        const endpoint = '/lab/workorders';
        return this.axiosAuthentifiedRequest.post(endpoint, payload);
    }
    updateLabWorkOrder(workOrderId: string, bodyParams: object): Promise<AxiosResponse> {
        const endpoint = `/lab/workorders/${workOrderId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    addLabToExistingWorkOrder(payload: object, labWorkOrderId: string): Promise<AxiosResponse> {
        const endpoint = `/lab/workorders/${labWorkOrderId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, payload);
    }
    getAnalysisRequestsTemplates(
        siteId: string,
        queryParams: Record<string, string | number | boolean> = {},
    ): Promise<AxiosResponse<AnalysisRequestTemplatesResponse>> {
        const endpoint = `/sites/${siteId}/analysisRequest/template`;
        return this.axiosAuthentifiedRequest.get(endpoint, { params: queryParams });
    }

    addAnalysisRequestTemplate(siteId: string, payload: object): Promise<AxiosResponse> {
        const endpoint = `/sites/${siteId}/analysisRequest/template`;
        return this.axiosAuthentifiedRequest.post(endpoint, payload);
    }

    getComponentTemplates(
        siteId: string,
        componentId: string,
    ): Promise<AxiosResponse<ComponentTemplatesResponse>> {
        const endpoint = `/sites/${siteId}/component/${componentId}/template`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    editAnalysisRequestTemplate(
        siteId: string,
        templateId: string,
        payload: object,
    ): Promise<AxiosResponse> {
        const endpoint = `/sites/${siteId}/analysisRequest/template/${templateId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, payload);
    }

    deleteAnalysisRequestsTemplates(siteId: string, templateId: string): Promise<AxiosResponse> {
        const endpoint = `/sites/${siteId}/analysisRequest/template/${templateId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    duplicateAnalysisRequestsTemplates(siteId: string, templateId: string): Promise<AxiosResponse> {
        const endpoint = `/sites/${siteId}/analysisRequest/template/${templateId}/duplicate`;
        return this.axiosAuthentifiedRequest.post(endpoint);
    }

    startAnalysisRequestsTemplates(siteId: string, templateId: string): Promise<AxiosResponse> {
        const endpoint = `/sites/${siteId}/analysisRequest/template/${templateId}/start`;
        return this.axiosAuthentifiedRequest.post(endpoint);
    }

    getComponentTreatment(
        siteId: string,
        componentId: string,
        type: string,
    ): Promise<AxiosResponse<ComponentTreatmentResponse>> {
        const endpoint = `/sites/${siteId}/component/${componentId}/treatement`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: { type },
        });
    }
    updateSummaryReport(workOrderId: string, bodyParams: object) {
        const endpoint = `/workorders/${workOrderId}/summaryreport`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }
    addFlowMeter(siteId: string, payload: FlowMeterPayload[]) {
        const endpoint = `sites/${siteId}/devices/flowMeters/`;
        return this.axiosAuthentifiedRequest.post(endpoint, payload);
    }
    getFlowMeter(siteId: string, flowMeterId: string) {
        const endpoint = `sites/${siteId}/devices/flowMeters/${flowMeterId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }
    updateFlowMeter(siteId: string, meterId: string, payload: FlowMeterPayload) {
        const endpoint = `sites/${siteId}/devices/flowMeters/${meterId}`;
        return this.axiosAuthentifiedRequest.put(endpoint, payload);
    }

    getSiteDocuments(siteId: string, queryParams: object): Promise<AxiosResponse<GetDocuments>> {
        const endpoint = `/sites/${siteId}/documents`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    getSiteDocument(siteId: string, documentId: string): Promise<AxiosResponse<SiteDocument>> {
        const endpoint = `/sites/${siteId}/documents/${documentId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    updateSiteDocument(siteId: string, documentId: string, formData: FormData) {
        const endpoint = `/sites/${siteId}/documents/${documentId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    }

    getDocumentTypes(params = { pagination: false }) {
        const endpoint = '/documents/types';
        return this.axiosAuthentifiedRequest.get(endpoint, { params });
    }

    addSiteDocument(siteId: string, formData: FormData) {
        const endpoint = `/sites/${siteId}/documents`;
        return this.axiosAuthentifiedRequest.post(endpoint, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    }

    addAnalysisRequest(bodyParams: object): Promise<AxiosResponse<AnalysisRequest>> {
        const endpoint = '/analysisRequests';
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    removeWorkOrderManyAnalysisOnComponent(
        workOrderId: string,
        activityId: string,
        bodyParams: object,
    ) {
        const endpoint = `/workorders/${workOrderId}/activity/${activityId}/analysisOnComponent`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    deleteSiteDocument(siteId: string, documentId: string) {
        const endpoint = `/sites/${siteId}/documents/${documentId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }
    getProcedures(
        queryParams: Record<string, string | number | boolean> = {},
    ): Promise<AxiosResponse<GetProceduresResponse>> {
        const endpoint = '/procedures';
        return this.axiosAuthentifiedRequest.get<GetProceduresResponse>(endpoint, {
            params: queryParams,
        });
    }

    getAssociateSiteProcedures(
        siteId: string,
        perpage: number = 25,
        page: number = 1,
        search?: string,
    ) {
        let endpoint = `/procedures/${siteId}/associateProcedure?perpage=${perpage}&page=${page}`;
        if (search) {
            endpoint += `&search=${encodeURIComponent(search)}`;
        }
        return this.axiosAuthentifiedRequest.get(endpoint);
    }
    updateAssociateSiteProcedures(siteId: string, payload: { procedures: string[] }) {
        const endpoint = `/procedures/${siteId}/associateProcedure`;
        return this.axiosAuthentifiedRequest.patch(endpoint, payload);
    }
    deleteProcedure(siteId: string, procedureId: string) {
        const endpoint = `/procedures/${siteId}/associateProcedure/${procedureId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    getFlowDiagrams(
        networkId: string,
        queryParams: object,
    ): Promise<AxiosResponse<FlowDiagramsResponse>> {
        const endpoint = `/networks/${networkId}/flowdiagrams`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    addFlowDiagram(
        networkID: string,
        bodyParams: FormData,
    ): Promise<AxiosResponse<FlowDiagramContainer>> {
        const endpoint = `/networks/${networkID}/flowdiagrams`;
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    }

    updateFlowDiagram(
        networkId: string,
        flowDiagramId: string,
        bodyParams: FormData,
    ): Promise<AxiosResponse<FlowDiagramContainer>> {
        const endpoint = `/networks/${networkId}/flowdiagrams/${flowDiagramId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    }

    getTeamInfo(teamId: string) {
        const endpoint = `/teams/${teamId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }
    getTeam(teamId: string): Promise<AxiosResponse<Team>> {
        const endpoint = `/teams/${teamId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    async getTeams(params: GetTeamsParams): Promise<GetTeamsRes> {
        const endpoint = '/teams';
        const response = await this.axiosAuthentifiedRequest.get(endpoint, { params });
        return response.data;
    }

    addTeam(payload: TeamPayload | FormData): Promise<AxiosResponse<Team>> {
        const endpoint = '/teams';
        const config =
            payload instanceof FormData
                ? { headers: { 'Content-Type': 'multipart/form-data' } }
                : undefined;
        return this.axiosAuthentifiedRequest.post(endpoint, payload, config);
    }

    updateTeam(teamId: string, payload: TeamPayload | FormData): Promise<AxiosResponse<Team>> {
        const endpoint = `/teams/${teamId}`;
        const config =
            payload instanceof FormData
                ? { headers: { 'Content-Type': 'multipart/form-data' } }
                : undefined;
        return this.axiosAuthentifiedRequest.patch(endpoint, payload, config);
    }

    removeTeam(teamId: string): Promise<AxiosResponse> {
        const endpoint = `/teams/${teamId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }
    getWOTemplateInfo(woTemplateId: string): Promise<AxiosResponse<WorkOrderTemplateInfoResponse>> {
        const endpoint = `/workorders/templates/${woTemplateId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    getWOTemplates(params: object): Promise<AxiosResponse<WorkOrderTemplatesResponse>> {
        const endpoint = '/workorders/templates';
        return this.axiosAuthentifiedRequest.get(endpoint, { params });
    }

    duplicateWOTemplate(woTemplateId: string, content: object) {
        const endpoint = `/workorders/templates/${woTemplateId}/duplicate`;
        return this.axiosAuthentifiedRequest.post(endpoint, content);
    }

    deleteWorkOrderTemplate(woTemplateId: string) {
        const endpoint = `/workorders/templates/${woTemplateId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    createWOFromTemplate(woTemplateId: string, bodyParams: object) {
        const endpoint = `/workorders/templates/${woTemplateId}`;
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    deleteWorkorder(workorderId: string) {
        const endpoint = `/workorders/${workorderId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    async getClientMembers(
        clientId: string,
        queryParams?: object,
    ): Promise<responseGetClientMembers> {
        const endpoint = `/clients/${clientId}/members`;
        const response = await this.axiosAuthentifiedRequest.get(endpoint, { params: queryParams });
        return response.data;
    }

    async getComments(workOrderId: string): Promise<AxiosResponse<Comment[]>> {
        const endpoint = `/workorders/${workOrderId}/comments`;
        const response = await this.axiosAuthentifiedRequest.get(endpoint);
        return response;
    }

    async getTeamMembers(
        teamId: string,
        role: string,
        extraParams = {},
    ): Promise<responseGetClientMembers> {
        const endpoint = `/teams/${teamId}/members`;
        let params;

        if (Array.isArray(role)) {
            params = { role: role.join(','), ...extraParams };
        } else {
            const initialParams = role === 'team' ? {} : { role };
            params = { ...initialParams, ...extraParams };
        }

        return this.axiosAuthentifiedRequest
            .get(endpoint, { params })
            .then((response) => response.data);
    }
    associateMemberToClient(memberId: string, bodyParams: { clientId: string }) {
        const endpoint = `/members/${memberId}/clients/`;
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }
    revokeClientToMember(memberId: string, clientId: string): Promise<AxiosResponse> {
        const endpoint = `/members/${memberId}/clients/${clientId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }
    editClientAccessGroup(clientId: string, bodyParams: { accessGroups: string[] }) {
        const endpoint = `/clients/${clientId}/accessgroups`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    removeMemberFromTeam(memberId: string) {
        const endpoint = `/members/${memberId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    editClientTeamMember(userId: string, bodyParams: ClientTeamMemberPayload) {
        const endpoint = `/users/${userId}/profile`;
        return this.axiosAuthentifiedRequest.put(endpoint, bodyParams);
    }

    addFlowMeterHub(siteId: string, payload: FlowMeterCreatePayload) {
        const endpoint = `/sites/${siteId}/devices/metersHubs`;
        return this.axiosAuthentifiedRequest.post(endpoint, payload);
    }
    deleteFlowMeterHub(siteId: string, meterHubId: string) {
        const endpoint = `/sites/${siteId}/devices/metersHubs/${meterHubId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }
    updateFlowMeterHub(siteId: string, meterHubId: string, payload: FlowMeterUpdatePayload) {
        const endpoint = `/sites/${siteId}/devices/metersHubs/${meterHubId}`;
        return this.axiosAuthentifiedRequest.put(endpoint, payload);
    }
    addControllerHub(siteId: string, payload: ControllerHubPayload) {
        const endpoint = `/sites/${siteId}/controllers`;
        return this.axiosAuthentifiedRequest.post(endpoint, payload);
    }
    deleteControllerHub(siteId: string, controllerHubId: string) {
        const endpoint = `/sites/${siteId}/controllers/${controllerHubId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }
    updateControllerHub(siteId: string, controllerHubId: string, payload: ControllerHubPayload) {
        const endpoint = `/sites/${siteId}/controllers/${controllerHubId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, payload);
    }

    addProductLevelSensor(siteId: string, payload: object) {
        const endpoint = `/sites/${siteId}/devices/productMeters`;
        return this.axiosAuthentifiedRequest.post(endpoint, payload);
    }
    deleteProductLevelSensor(siteId: string, productLevelSensorId: string) {
        const endpoint = `/sites/${siteId}/devices/productMeters/${productLevelSensorId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }
    updateProductLevelSensor(siteId: string, productLevelSensorId: string, payload: object) {
        const endpoint = `/sites/${siteId}/devices/productMeters/${productLevelSensorId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, payload);
    }

    updateflowMeterDetail(
        siteId: string,
        flowMeterId: string,
        payload: FlowMeterDetailUpdatePayload,
    ) {
        const endpoint = `/sites/${siteId}/devices/flowMeters/${flowMeterId}`;
        return this.axiosAuthentifiedRequest.put(endpoint, payload);
    }

    updateMeterHubConfiguration(
        siteId: string,
        meterHubId: string,
        params: object,
    ): Promise<AxiosResponse> {
        const endpoint = `/sites/${siteId}/devices/metersHubs/${meterHubId}/linkMeters`;
        return this.axiosAuthentifiedRequest.put(endpoint, params);
    }

    updateControllerHubDetail(
        siteId: string,
        controllerHubId: string,
        payload: ProbeUpdatePayload,
    ) {
        const endpoint = `/sites/${siteId}/controllersDetails/${controllerHubId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, payload);
    }

    editProbe(
        siteId: string,
        controllerId: string,
        probeId: string,
        payload: ProbeUpdatePayload,
    ): Promise<AxiosResponse> {
        const endpoint = `/sites/${siteId}/controllers/${controllerId}/probes/${probeId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, payload);
    }

    updateControllerDetails(
        siteId: string,
        controllerHubId: string,
        payload: ControllerHubPayload,
    ) {
        const endpoint = `/sites/${siteId}/controllers/${controllerHubId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, payload);
    }

    addDeleteORbothProbes(
        siteId: string,
        controllerHubId: string,
        payload: { probes: ProbeUpdatePayload[] },
    ) {
        const endpoint = `/sites/${siteId}/controllers/${controllerHubId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, payload);
    }
    deleteflowMeterDetail(siteId: string, meterHubId: string, meterId: string) {
        const endpoint = `/sites/${siteId}/devices/metersHubs/${meterHubId}/deleteMeter/${meterId}`;
        return this.axiosAuthentifiedRequest.put(endpoint);
    }
    deleteControllerHubDetail(siteId: string, controllerHubId: string, probeId: string) {
        const endpoint = `/sites/${siteId}/controllers/${controllerHubId}/probes/${probeId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    getFlowMeterHub(
        siteId: string,
        params: { page: number; limit?: number; search: string },
    ): Promise<AxiosResponse> {
        const endpoint = `/sites/${siteId}/devices/metersHubs/`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params,
        });
    }
    getControllersHub(siteId: string, queryParams: GetParamsStandard): Promise<AxiosResponse> {
        const endpoint = `/sites/${siteId}/controllers`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }
    getControllers(siteId: string, queryParams: GetParamsStandard): Promise<AxiosResponse> {
        const endpoint = `/sites/${siteId}/controllers`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }
    unlinkFlowMeter(analysisConfigurationId: string): Promise<AxiosResponse> {
        const endpoint = `/analyses/analysisconfigurations/${analysisConfigurationId}/unlinkFlowMeter`;
        return this.axiosAuthentifiedRequest.patch(endpoint);
    }
    unlinkProductLevel(analysisConfigurationId: string): Promise<AxiosResponse> {
        const endpoint = `/analyses/analysisconfigurations/${analysisConfigurationId}/unlinkProductMeter`;
        return this.axiosAuthentifiedRequest.patch(endpoint);
    }
    getProductContainers(bodyParams: { search: string }): Promise<AxiosResponse> {
        const endPoint = '/productcontainers';
        return this.axiosAuthentifiedRequest.get(endPoint, { params: bodyParams });
    }
    convertProductLevel(payload: {
        containerId: string;
        containerCustom?: Record<string, unknown> | null;
        readingValue: number;
        readingUnit: string;
        reportUnit: string;
        siteProductId?: string | null;
    }): Promise<AxiosResponse> {
        const endPoint = '/productcontainers/convert';
        return this.axiosAuthentifiedRequest.post(endPoint, payload);
    }
    linkProductLevel(
        analysisConfigurationId: string,
        payload: ProductLevelPayload,
    ): Promise<AxiosResponse> {
        const endpoint = `/analyses/analysisconfigurations/${analysisConfigurationId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, payload);
    }
    unlinkFuelMeter(analysisConfigurationId: string): Promise<AxiosResponse> {
        const endpoint = `/analyses/analysisconfigurations/${analysisConfigurationId}/unlinkFuelMeter`;
        return this.axiosAuthentifiedRequest.patch(endpoint);
    }
    unlinkFlowMeterRate(analysisConfigurationId: string): Promise<AxiosResponse> {
        const endpoint = `/analyses/analysisconfigurations/${analysisConfigurationId}/unlinkFlowMeterRate`;
        return this.axiosAuthentifiedRequest.patch(endpoint);
    }
    addFuelMeter(siteId: string, params: FuelMeterPayload): Promise<AxiosResponse> {
        const endpoint = `/sites/${siteId}/devices/fuelMeters/`;
        return this.axiosAuthentifiedRequest.post(endpoint, params);
    }
    updateFuelMeter(
        siteId: string,
        meterId: string,
        payload: FuelMeterPayload,
    ): Promise<AxiosResponse> {
        const endpoint = `/sites/${siteId}/devices/fuelMeters/${meterId}`;
        return this.axiosAuthentifiedRequest.put(endpoint, payload);
    }

    addFlowMeterRate(siteId: string, params: FlowMeterRatePayload): Promise<AxiosResponse> {
        const endpoint = `/sites/${siteId}/devices/flowMeterRate/`;
        return this.axiosAuthentifiedRequest.post(endpoint, params);
    }
    updateFlowMeterRate(
        siteId: string,
        meterId: string,
        payload: FlowMeterRatePayload,
    ): Promise<AxiosResponse> {
        const endpoint = `/sites/${siteId}/devices/flowMeterRate/${meterId}`;
        return this.axiosAuthentifiedRequest.put(endpoint, payload);
    }
    getAvailableProbes(
        siteId: string,
        controllerId: string,
    ): Promise<AxiosResponse<AvailableProbesResponse>> {
        const endpoint = `/sites/${siteId}/controllers/${controllerId}/probes/available`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }
    getComponentConfiguration(
        analysisConfigurationId: string,
        params = {},
    ): Promise<AxiosResponse<ComponentConfigurationResponse>> {
        const endpoint = `/analyses/analysisconfigurations/${analysisConfigurationId}`;
        return this.axiosAuthentifiedRequest.get(endpoint, { params });
    }
    unlinkProbe(analysisConfigurationId: string): Promise<AxiosResponse> {
        const endpoint = `/analyses/analysisconfigurations/${analysisConfigurationId}/unlinkProbe`;
        return this.axiosAuthentifiedRequest.patch(endpoint);
    }
    getAllProductSensorsHub(
        siteId: string,
        queryParams: { search: string },
    ): Promise<AxiosResponse> {
        const endpoint = `sites/${siteId}/devices/productMeters`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    getContact(contactId: string): Promise<AxiosResponse> {
        const endpoint = `/contacts/${contactId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    getContacts(queryParams: GetContactsParams): Promise<AxiosResponse<ContactsResponse>> {
        const endpoint = '/contacts';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    updateContactReceiveReport(
        contactId: string,
        payload: { siteId: string; receiveLabReport?: boolean; receiveServiceReport?: boolean },
    ) {
        const endpoint = `/contacts/${contactId}/receiveReport`;
        return this.axiosAuthentifiedRequest.patch(endpoint, payload);
    }

    getStates(country: string): Promise<AxiosResponse<State[]>> {
        const endpoint = `/world/states`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: { country },
        });
    }

    getCountries(): Promise<AxiosResponse<Country[]>> {
        const endpoint = `/world/countries`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    getTimezones(): Promise<AxiosResponse<{ timezones: string[] }>> {
        const endpoint = `/world/timezones`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    deleteContact(contactId: string) {
        const endpoint = `/contacts/${contactId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }
    addContact(payload: ContactFormData): Promise<AxiosResponse<Contact>> {
        const endpoint = '/contacts';
        return this.axiosAuthentifiedRequest.post(endpoint, payload);
    }
    updateContact(contactId: string, payload: ContactFormData): Promise<AxiosResponse<Contact>> {
        const endpoint = `/contacts/${contactId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, payload);
    }

    reactivateContact(contactId: string): Promise<AxiosResponse> {
        const endpoint = `/contacts/${contactId}/reactivate`;
        return this.axiosAuthentifiedRequest.patch(endpoint);
    }
    getAnalysisConfiguration(
        configurationId: string,
    ): Promise<AxiosResponse<AnalysisConfigurationResponse>> {
        const endpoint = `/analyses/analysisconfigurations/${configurationId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    updateAnalysisConfig(componentConfigurationId: string, configurationInfo: ConfigurationInfo) {
        const endpoint = `/analyses/analysisconfigurations/${componentConfigurationId}`;

        // PUT not implemented yet
        return this.axiosAuthentifiedRequest.patch(endpoint, configurationInfo);
    }

    getSiteChangeEvents(
        siteId: string,
        clientId: string,
        queryParams: GetSiteChangeEventsParams = {},
    ): Promise<AxiosResponse<SiteChangeEventsResponse>> {
        const endpoint = `/logs/changeevents/sites/${siteId}/clients/${clientId}`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }
    getSiteReports(
        siteId: string,
        params: GetSiteReportsParams,
    ): Promise<AxiosResponse<SiteReportsResponse>> {
        const endpoint = `/sites/${siteId}/reports`;
        return this.axiosAuthentifiedRequest.get(endpoint, { params });
    }
    createConsolidatedReport(
        siteId: string,
        payload: ConsolidatedReportPayload,
    ): Promise<AxiosResponse<ConsolidatedReportResponse>> {
        const endpoint = `/sites/${siteId}/reports/consolidated`;
        return this.axiosAuthentifiedRequest.post(endpoint, payload, {
            suppressErrorToastStatuses: [409, 422],
        });
    }

    getSiteAlerts(
        siteId: string,
        params: GetSiteAlertsParams = {},
    ): Promise<AxiosResponse<SiteAlertsResponse>> {
        const endpoint = `/sites/${siteId}/alerts`;
        return this.axiosAuthentifiedRequest.get(endpoint, { params });
    }

    getSiteAlert(siteId: string, alertId: string): Promise<AxiosResponse<SiteAlert>> {
        const endpoint = `/sites/${siteId}/alerts/${alertId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    createSiteAlert(siteId: string, payload: object): Promise<AxiosResponse> {
        const endpoint = `/sites/${siteId}/alerts`;
        return this.axiosAuthentifiedRequest.post(endpoint, payload);
    }

    updateSiteAlert(siteId: string, alertId: string, payload: object): Promise<AxiosResponse> {
        const endpoint = `/sites/${siteId}/alerts/${alertId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, payload);
    }

    deleteAlert(alertId: string, siteId: string) {
        const endpoint = `sites/${siteId}/alerts/${alertId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    updateAlertSubscription(
        siteId: string,
        alertId: string,
        payload: { sms: boolean; email: boolean; app: boolean },
    ): Promise<AxiosResponse> {
        const endpoint = `/sites/${siteId}/alerts/${alertId}/subscribe`;
        return this.axiosAuthentifiedRequest.patch(endpoint, payload);
    }

    getActivityTypes(): Promise<AxiosResponse<ActivityTypesResponse>> {
        const endpoint = '/activities/types';
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    getLabWorkOrder(workOrderId: string): Promise<AxiosResponse<SiteReportLabWorkOrder>> {
        const endpoint = `/lab/workorders/${workOrderId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    getWorkOrder(workOrderId: string): Promise<AxiosResponse<SiteReportWorkOrder>> {
        const endpoint = `/workorders/${workOrderId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }
    getDashboardLayout(memberId: string, siteId: string): Promise<AxiosResponse> {
        const endpoint = `/dashboard/member/${memberId}/site/${siteId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }
    getGlobalProductLevelSensors(): Promise<AxiosResponse<{ sensors: SensorItem[] }>> {
        const endpoint = '/dashboard/product-level-sensors';
        return this.axiosAuthentifiedRequest.get(endpoint);
    }
    getSiteDailySummary(siteId: string): Promise<AxiosResponse<GetSiteDailySummaryRes>> {
        const endpoint = `/dashboard/site/${siteId}/daily-summary`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }
    getSiteProductConsumptionWorkOrders(
        siteId: string,
    ): Promise<AxiosResponse<ProductConsumptionWorkOrdersResponse>> {
        const endpoint = `/dashboard/site/${siteId}/product-consumption`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }
    saveDashboardLayout(
        memberId: string,
        siteId: string,
        payload: DashboardTemplateTypes[],
    ): Promise<AxiosResponse> {
        const endpoint = `dashboard/member/${memberId}/site/${siteId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, payload);
    }
    getWorkOrders(queryParams: QueryParams): Promise<AxiosResponse<ListResponse<ServiceWorker[]>>> {
        const endpoint = '/workorders';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }
    updateWorkOrder(workOrderId: string, params: object): Promise<AxiosResponse> {
        const endpoint = `/workorders/${workOrderId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, params);
    }

    getWorkOrderReportPreview(workOrderId: string): Promise<AxiosResponse> {
        const endpoint = `/workorders/${workOrderId}/summaryreport/preview`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            responseType: 'blob',
        });
    }

    getWorkOrderHtmlReportPreview(workOrderId: string): Promise<AxiosResponse<string>> {
        const endpoint = `/workorders/${workOrderId}/summaryreport/html-preview`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            responseType: 'text',
        });
    }

    sendWorkOrderReportToUsers(workOrderId: string, bodyParams: object): Promise<AxiosResponse> {
        const endpoint = `/workorders/${workOrderId}/summaryreport/sendEmailToUsers`;
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    getAvailableSiteUsers(siteId: string): Promise<AxiosResponse> {
        const endpoint = `/sites/${siteId}/allusers`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    getSalesGroupLeadersByClient(clientId: string): Promise<AxiosResponse> {
        const endpoint = `/clients/${clientId}/salesGroupLeaders/`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }
    deleteWorkOrder(workOrderId: string): Promise<AxiosResponse> {
        const endpoint = `/workorders/${workOrderId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }
    unlockWorkOrder(workOrderId: string): Promise<AxiosResponse> {
        const endpoint = `/workorders/${workOrderId}/unlock`;
        return this.axiosAuthentifiedRequest.patch(endpoint);
    }
    exportWorkOrdersToCSV(queryParams: {
        startDate: string;
        endDate: string;
        accessGroups: string[];
    }): Promise<AxiosResponse> {
        const endpoint = '/teams/exportWorkordersCSV';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
            responseType: 'blob',
        });
    }
    getSiteNotificationDetail(siteId: string, notificationId: string): Promise<AxiosResponse> {
        const endpoint = `/sites/${siteId}/alerts/notifications/${notificationId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }
    getAllNotificationsBySite(
        siteId: string,
        queryParams: object,
    ): Promise<AxiosResponse<SiteNotificationsResponse>> {
        const endpoint = `/sites/${siteId}/alerts/notifications`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    getAllNotifications(queryParams: object): Promise<AxiosResponse<NotificationsResponse>> {
        const endpoint = '/alerts/notifications';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    updateAcknowledge(notificationId: string, payload: { status: string }): Promise<AxiosResponse> {
        const endpoint = `/alerts/notifications/${notificationId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, payload);
    }

    notificationDetails(
        notificationId: string,
    ): Promise<AxiosResponse<NotificationDetailsResponse>> {
        const endpoint = `/alerts/notifications/${notificationId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    getUnacknowledgedNotificationsCount(): Promise<AxiosResponse<{ count: number }>> {
        const endpoint = '/alerts/notifications/unacknowledged';
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    // Graphics APIs
    getGraphics(siteId: string): Promise<AxiosResponse<GraphicsResponse>> {
        const endpoint = `/sites/${siteId}/graphics`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }
    getGraphic(siteId: string, graphicId: string): Promise<AxiosResponse<GraphicItem>> {
        const endpoint = `/sites/${siteId}/graphics/${graphicId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    addGraphic(siteId: string, bodyParams: object): Promise<AxiosResponse> {
        const endpoint = `/sites/${siteId}/graphics`;
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    editGraphic(siteId: string, graphicId: string, bodyParams: object): Promise<AxiosResponse> {
        const endpoint = `/sites/${siteId}/graphics/${graphicId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    getComponentsWithConsumptionAnalysis(siteId: string, params: object): Promise<AxiosResponse> {
        const endpoint = `/sites/${siteId}/componentsWithConsumptionAnalysis`;
        return this.axiosAuthentifiedRequest.get(endpoint, { params });
    }

    getConsumptionByComponent(componentId: string, params: object): Promise<AxiosResponse> {
        const endpoint = `/components/${componentId}/consumptionAnalysis`;
        return this.axiosAuthentifiedRequest.get(endpoint, { params });
    }

    deleteGraphic(siteId: string, graphicId: string): Promise<AxiosResponse> {
        const endpoint = `/sites/${siteId}/graphics/${graphicId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    getSeries(siteId: string, graphicId: string): Promise<AxiosResponse<SeriesResponse>> {
        const endpoint = `/sites/${siteId}/graphics/${graphicId}/series`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    addSerie(
        siteId: string,
        graphicId: string,
        bodyParams: AddSeriePayload,
    ): Promise<AxiosResponse> {
        const endpoint = `/sites/${siteId}/graphics/${graphicId}/series`;
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    updateSerie(
        siteId: string,
        graphicId: string,
        serieId: string,
        bodyParams: object,
    ): Promise<AxiosResponse> {
        const endpoint = `/sites/${siteId}/graphics/${graphicId}/series/${serieId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    deleteSerie(siteId: string, graphicId: string, serieId: string): Promise<AxiosResponse> {
        const endpoint = `/sites/${siteId}/graphics/${graphicId}/series/${serieId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    async getSeriesData<T = unknown>(
        siteId: string,
        graphicId: string,
        bodyParams: Record<string, unknown>,
    ): Promise<T> {
        const endpoint = `/sites/${siteId}/graphics/${graphicId}/data`;
        const { data } = await this.axiosAuthentifiedRequest.get(endpoint, { params: bodyParams });
        return data as T;
    }

    async getSerieData<T = unknown>(
        siteId: string,
        graphicId: string,
        serieId: string,
        bodyParams: Record<string, unknown>,
    ): Promise<T> {
        const endpoint = `/sites/${siteId}/graphics/${graphicId}/series/${serieId}/data`;
        const { data } = await this.axiosAuthentifiedRequest.get(endpoint, { params: bodyParams });
        return data as T;
    }

    getFeatures(params?: { search?: string }): Promise<AxiosResponse<Feature[]>> {
        const endpoint = '/features';
        return this.axiosAuthentifiedRequest.get(endpoint, { params });
    }

    deactivateFeature(featureId: string, teamId: string): Promise<AxiosResponse> {
        const endpoint = `/features/deactivate?featureId=${featureId}&teamId=${teamId}`;
        return this.axiosAuthentifiedRequest.put(endpoint);
    }

    activateFeature(featureId: string, teamId: string): Promise<AxiosResponse> {
        const endpoint = `/features/activate?featureId=${featureId}&teamId=${teamId}`;
        return this.axiosAuthentifiedRequest.put(endpoint);
    }

    getSuperAdminFeatures(params?: { search?: string }): Promise<AxiosResponse<Feature[]>> {
        const endpoint = '/features/super-admin';
        return this.axiosAuthentifiedRequest.get(endpoint, { params });
    }

    superAdminToggleFeature(
        featureId: string,
        teamId: string,
        isActive: boolean,
    ): Promise<AxiosResponse> {
        const action = isActive ? 'deactivate' : 'activate';
        const endpoint = `/features/super-admin/${action}?featureId=${featureId}&teamId=${teamId}`;
        return this.axiosAuthentifiedRequest.put(endpoint);
    }

    patchSuperAdminFeatureTeam(
        featureId: string,
        teamId: string,
        activate: boolean,
    ): Promise<AxiosResponse> {
        const endpoint = '/features/super-admin';
        return this.axiosAuthentifiedRequest.patch(endpoint, { featureId, teamId, activate });
    }

    updateFeature(
        featureId: string,
        payload: {
            key: string;
            type: string;
            description?: { [key: string]: string };
            price?: number | null;
        },
    ): Promise<AxiosResponse> {
        const endpoint = `/features?id=${featureId}`;
        return this.axiosAuthentifiedRequest.put(endpoint, payload);
    }

    addElementNetwork(
        networkId: string,
        payload: { nodeType: string; nodeTypeToRedirect: string; type: string; name: string },
    ): Promise<AxiosResponse> {
        const endpoint = '/networks/' + networkId + '/nodes';
        return this.axiosAuthentifiedRequest.post(endpoint, { ...payload, networkId });
    }

    getSiteServiceCallStatistics(
        siteId: string,
        queryParams: object,
    ): Promise<AxiosResponse<ServiceCallType[]>> {
        const endpoint = `/sites/${siteId}/serviceCallStatistics`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }
    getBilling(
        queryParams: object,
    ): Promise<AxiosResponse<ListResponse<ServiceWorkOrderBillingResponse>>> {
        const endpoint = '/workorders/billing';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }
    getLabBillings(queryParams: object): Promise<AxiosResponse<ListResponse<LabBillingType[]>>> {
        const endpoint = '/lab/billing';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }
    getLabBilling(labWorkOrderId: string): Promise<AxiosResponse<getLabBillingAnalysisRequest>> {
        const endpoint = `/lab/billing/${labWorkOrderId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }
    billingStatusUpdate(id: string, bodyParams: { invoiced: boolean }): Promise<AxiosResponse> {
        const endpoint = `/workorders/${id}/billingStatus`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }
    labBillingStatusUpdate(id: string, bodyParams: { invoiced: boolean }): Promise<AxiosResponse> {
        const endpoint = `/lab/workorders/${id}/billingStatus`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }
    getSiteE4Economies(
        siteId: string,
        params: { startDate: string | null; endDate: string | null },
    ): Promise<AxiosResponse<E4EconomyType>> {
        const endpoint = `/sites/${siteId}/e4economy`;
        return this.axiosAuthentifiedRequest.get(endpoint, { params });
    }

    getSiteOrders(
        siteId: string,
        queryParams?: object,
    ): Promise<AxiosResponse<SiteOrdersResponse>> {
        const endpoint = `/sites/${siteId}/order`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    updateSiteOrder(
        siteId: string,
        orderId: string,
        payload: { note: string },
    ): Promise<AxiosResponse<UpdatedSiteOrder>> {
        const endpoint = `/sites/${siteId}/order/${orderId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, payload);
    }

    deleteSiteOrder(siteId: string, orderId: string): Promise<AxiosResponse<void>> {
        const endpoint = `/sites/${siteId}/order/${orderId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    orderSiteOrder(siteId: string, orderId: string): Promise<AxiosResponse<void>> {
        const endpoint = `/sites/${siteId}/order/${orderId}`;
        return this.axiosAuthentifiedRequest.post(endpoint);
    }

    getProductsBySiteId<T = { products: Product[] }>(siteId: string): Promise<AxiosResponse<T>> {
        const endpoint = `/sites/${siteId}/products?pagination=false`;
        return this.axiosAuthentifiedRequest.get<T>(endpoint);
    }

    updateProductQuantity(
        siteId: string,
        productData: {
            productInventory: string;
            id: string;
            productName: string;
            location: string;
            container: string;
            unit: string;
            quantity: string;
        },
    ): Promise<AxiosResponse<UpdatedProductQuantity>> {
        const endpoint = `/sites/${siteId}/order/`;
        return this.axiosAuthentifiedRequest.post(endpoint, productData);
    }

    updateSiteOrderPo(
        siteId: string,
        orderId: string,
        payload: { po: string },
    ): Promise<AxiosResponse<UpdatedSiteOrder>> {
        const endpoint = `/sites/${siteId}/order/${orderId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, payload);
    }
    getProducts(queryParams: GetProductsParams): Promise<AxiosResponse<ProductsResponse>> {
        const endpoint = '/products';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }
    getSiteInventoryProducts(
        siteId: string,
    ): Promise<AxiosResponse<SiteInventoryProductsResponse>> {
        const endpoint = `/sites/${siteId}/products/inventories`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }
    getOrders(
        siteId: string,
        params: GetOrdersParams = {},
    ): Promise<AxiosResponse<SiteOrdersResponse>> {
        const endpoint = `/sites/${siteId}/order/`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params,
        });
    }
    getProduct(
        productId: string,
        queryParams: GetProductParams,
    ): Promise<AxiosResponse<ProductDetailResponse>> {
        const endpoint = `/products/${productId}`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }
    getSiteProduct(
        siteId: string,
        productId: string,
        queryParams: GetProductParams,
    ): Promise<AxiosResponse<ProductDetailResponse>> {
        const endpoint = `/sites/${siteId}/products/${productId}`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }
    getClientProduct(
        clientId: string,
        productId: string,
        queryParams: GetProductParams,
    ): Promise<AxiosResponse<ProductDetailResponse>> {
        const endpoint = `/clients/${clientId}/products/${productId}`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }
    getProductInventory(
        siteId: string,
        productId: string,
        queryParams: GetProductInventoryParams,
    ): Promise<AxiosResponse<ProductInventoryResponse>> {
        const endpoint = `/sites/${siteId}/products/${productId}/inventory`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }
    getProductInventoryItem(
        siteId: string,
        productId: string,
        inventoryId: string,
        queryParams: GetProductInventoryItemParams,
    ): Promise<AxiosResponse<ProductInventoryItem>> {
        const endpoint = `/sites/${siteId}/products/${productId}/inventory/${inventoryId}`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }
    addProductInventoryItem(
        siteId: string,
        productId: string,
        bodyParams: AddProductInventoryPayload,
    ): Promise<AxiosResponse<ProductInventoryItem>> {
        const endpoint = `/sites/${siteId}/products/${productId}/inventory`;
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }
    deleteSiteNode(nodeId: string, siteId: string): Promise<AxiosResponse> {
        const endpoint = `/sites/${siteId}/nodes/${nodeId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }
    restoreSiteNode(nodeId: string, siteId: string): Promise<AxiosResponse> {
        const endpoint = `/sites/${siteId}/nodes/${nodeId}/restore`;
        return this.axiosAuthentifiedRequest.patch(endpoint);
    }

    reorderSiteTree(siteId: string, bodyParams: ReorderSiteNodesPayload): Promise<AxiosResponse> {
        const endpoint = `/sites/${siteId}/nodes/reorder`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }
    updatedProductInventoryItem(
        siteId: string,
        productId: string,
        inventoryId: string,
        bodyParams: UpdateProductInventoryPayload,
    ): Promise<AxiosResponse<ProductInventoryItem>> {
        const endpoint = `/sites/${siteId}/products/${productId}/inventory/${inventoryId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }
    removeProductInventoryItem(
        siteId: string,
        productId: string,
        inventoryId: string,
    ): Promise<AxiosResponse<void>> {
        const endpoint = `/sites/${siteId}/products/${productId}/inventory/${inventoryId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }
    addItemToOrder(
        siteId: string,
        bodyParams: AddItemToOrderPayload,
    ): Promise<AxiosResponse<UpdatedProductQuantity>> {
        const endpoint = `/sites/${siteId}/order/`;
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }
    getSiteProducts(
        siteId: string,
        queryParams: GetProductsParams,
    ): Promise<AxiosResponse<SiteProductsResponse>> {
        const endpoint = `/sites/${siteId}/products`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }
    deleteSiteProduct(siteId: string, productId: string): Promise<AxiosResponse<void>> {
        const endpoint = `/sites/${siteId}/products/${productId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    updateDisplayNote(memberId: string, params: DisplayNoteData): Promise<AxiosResponse> {
        const endpoint = `/members/${memberId}/updateDisplayNote`;
        return this.axiosAuthentifiedRequest.patch(endpoint, params);
    }

    associateSiteProduct(
        siteId: string,
        bodyParams: AssociateSiteProductPayload,
    ): Promise<AxiosResponse<void>> {
        const endpoint = `/sites/${siteId}/products`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }
    updateSiteProduct(
        siteId: string,
        productId: string,
        bodyParams: UpdateSiteProductPayload,
    ): Promise<AxiosResponse<void>> {
        const endpoint = `/sites/${siteId}/products/${productId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    getDefaultAnalysesStats(
        queryParams?: object,
    ): Promise<AxiosResponse<DefaultAnalysesStatsResponse>> {
        const endpoint = '/analyses/defaults/stats/';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    getDefaultAnalyses(queryParams: {
        networkType?: string;
        componentType: string;
    }): Promise<AxiosResponse<DefaultAnalysesResponse>> {
        const endpoint = '/analyses/defaults/';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    resendInvitation(teamId: string, queryParams: object): Promise<AxiosResponse> {
        const endpoint = `/teams/${teamId}/members/invite/resend`;
        return this.axiosAuthentifiedRequest.post(
            endpoint,
            {},
            {
                params: queryParams,
            },
        );
    }

    addDefaultAnalysis(payload: {
        componentType: string;
        networkType: string;
        analysisType: string;
    }): Promise<AxiosResponse> {
        const endpoint = '/analyses/defaults/';
        return this.axiosAuthentifiedRequest.post(endpoint, payload);
    }

    putDefaultAnalysis(bodyParams: {
        componentType: string;
        networkType: string;
        analysisTypeIds: string[];
        oldComponentType: string;
        oldNetworkType: string;
    }): Promise<AxiosResponse> {
        const endpoint = '/analyses/defaults/';
        return this.axiosAuthentifiedRequest.put(endpoint, bodyParams);
    }

    submitWorkOrder(workOrderId: string, payload?: object): Promise<AxiosResponse> {
        const endpoint = `/workorders/${workOrderId}/submit`;
        return this.axiosAuthentifiedRequest.put(endpoint, payload);
    }

    deleteDefaultAnalysis(id: string): Promise<AxiosResponse> {
        const endpoint = `/analyses/defaults/${id}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }
    copySiteTreeFromAnotherSite(siteId: string, payload: CopySitePayload): Promise<AxiosResponse> {
        const endpoint = `/sites/${siteId}/copySiteTreeFromAnotherSite`;
        return this.axiosAuthentifiedRequest.post(endpoint, payload);
    }

    getRiskAnalyses(networkId: string, componentId: string): Promise<AxiosResponse<RiskAnalysis>> {
        const endpoint = `/riskAnalyses?${networkId}&componentId${componentId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    getLegionellaRiskAssessmentComponent(
        networkId: string,
        componentId: string,
    ): Promise<AxiosResponse<RiskAnalysis>> {
        const endpoint = `/legionella/riskassessments?networkId=${networkId}&componentId=${componentId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    updateLegionellaRiskAssessment(
        id: string,
        payload: LegionellaRiskAssessmentPayload,
    ): Promise<AxiosResponse<LegionellaRiskAssessmentResponse>> {
        const endpoint = `/legionella/riskassessments/${id}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, payload);
    }

    addNewLegionellaRiskAssessment(
        payload: LegionellaRiskAssessmentPayload,
    ): Promise<AxiosResponse<LegionellaRiskAssessmentResponse>> {
        const endpoint = '/legionella/riskassessments';
        return this.axiosAuthentifiedRequest.post(endpoint, payload);
    }

    getLegionellaRiskAssessementNetwork(
        id: string,
    ): Promise<AxiosResponse<LegionellaRiskAssessmentResponse>> {
        const endpoint = `/legionella/riskassessments/${id}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    getQuestionsLEgionellaRiskAssessment(): Promise<
        AxiosResponse<LegionellaRiskAssessmentQuestion[]>
    > {
        const endpoint = '/legionella/riskfactors';
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    updateRiskAnalysis(
        riskAnalysisId: string,
        bodyParams: RiskAnalysisPayload,
    ): Promise<AxiosResponse<RiskAnalysisPayload>> {
        const endpoint = `/riskanalyses/${riskAnalysisId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    deleteRiskAnalysis(riskAnalysisId: string): Promise<AxiosResponse> {
        const endpoint = `/riskanalyses/${riskAnalysisId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    addNewRiskAnalysis(payload: RiskAnalysisPayload): Promise<AxiosResponse<RiskAnalysisPayload>> {
        const endpoint = `/riskAnalyses`;
        return this.axiosAuthentifiedRequest.post(endpoint, payload);
    }

    createFlowDiagram(networkId: string, file: File, componentId: string): Promise<AxiosResponse> {
        const endpoint = `/networks/${networkId}/flowdiagrams`;
        const formData = new FormData();
        formData.append('diagram', file);
        formData.append('component', componentId);
        return this.axiosAuthentifiedRequest.post(endpoint, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    }
    async getRiskAnalysis(networkId: string, component?: string) {
        const params = {
            network: networkId,
            ...(component && { component }),
        };

        return this.axiosAuthentifiedRequest.get('/riskanalyses', { params });
    }

    getSectionsRiskAnalysis(analysisId: string): Promise<AxiosResponse<RiskAnalysisResponse>> {
        const endpoint = `/riskanalyses/${analysisId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    getRelease(queryParams: object): Promise<AxiosResponse<ReleaseNote>> {
        const endpoint = '/release';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    updateRelease(params: object): Promise<AxiosResponse<ReleaseNote>> {
        const endpoint = '/release';
        return this.axiosAuthentifiedRequest.patch(endpoint, params);
    }

    deleteFlowDiagram(
        networkId: string,
        flowDiagramId: string,
        fileId: string,
    ): Promise<AxiosResponse> {
        const endpoint = `/networks/${networkId}/flowdiagrams/${flowDiagramId}/file/${fileId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }
    getCertificatesHistory(
        networkId: string,
        queryParams: object,
    ): Promise<AxiosResponse<GetCertificatesHistoryResponse>> {
        const endpoint = `/networks/${networkId}/certificates/history`;
        return this.axiosAuthentifiedRequest.get(endpoint, { params: queryParams });
    }
    getCertificates(
        networkId: string,
        queryParams?: object,
    ): Promise<AxiosResponse<GetCertificatesResponse[]>> {
        const endpoint = `/networks/${networkId}/certificates`;
        return this.axiosAuthentifiedRequest.get(endpoint, { params: queryParams });
    }
    addCertificate(networkId: string, bodyParams: FormData): Promise<AxiosResponse> {
        const endpoint = `/networks/${networkId}/certificates`;
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    }
    getLegionellaControlCharts(
        networkId: string,
        componentId: string,
    ): Promise<AxiosResponse<LegionellaControlChartsResponse>> {
        const endpoint = `/networks/${networkId}/legionellaControlCharts`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: { component: componentId },
        });
    }

    uploadLegionellaControlChart(
        networkId: string,
        formData: FormData,
    ): Promise<AxiosResponse<unknown>> {
        const endpoint = `/networks/${networkId}/legionellaControlCharts`;
        return this.axiosAuthentifiedRequest.post(endpoint, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    }
    editCertificate(
        networkId: string,
        certificateId: string,
        bodyParams: object,
    ): Promise<AxiosResponse> {
        const endpoint = `/networks/${networkId}/certificates/${certificateId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }
    deleteCertificate(networkId: string, certificateId: string): Promise<AxiosResponse> {
        const endpoint = `/networks/${networkId}/certificates/${certificateId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }
    getNetworkInfo(networkId: string): Promise<AxiosResponse<NetworkOrComponentState>> {
        const endpoint = `/networks/${networkId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }
    getComponentInfo(componentId: string): Promise<AxiosResponse<NetworkOrComponentState>> {
        const endpoint = `/components/${componentId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    updateLegionellaControlChart(
        networkId: string,
        chartId: string,
        formData: FormData,
    ): Promise<AxiosResponse<LegionellaControlChart>> {
        const endpoint = `/networks/${networkId}/legionellaControlCharts/${chartId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    }

    addLegionellaControlChart(
        networkId: string,
        bodyParams: FormData,
    ): Promise<AxiosResponse<LegionellaControlChart>> {
        const endpoint = `/networks/${networkId}/legionellaControlCharts`;
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    }

    deleteLegionellaControlChart(
        networkId: string,
        chartId: string,
        fileId: string,
    ): Promise<AxiosResponse> {
        const endpoint = `/networks/${networkId}/legionellaControlCharts/${chartId}/file/${fileId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    getAnalysisCategories(params: {
        perpage?: number;
        page?: number;
        order?: string;
        search?: string;
    }): Promise<AxiosResponse<AnalysisCategoriesResponse>> {
        const endpoint = '/analyses/categories';
        return this.axiosAuthentifiedRequest.get(endpoint, { params });
    }

    getAnalysisCategory(categoryId: string): Promise<AxiosResponse<AnalysisCategory>> {
        const endpoint = `/analyses/categories/${categoryId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    updateAnalysisCategory(
        categoryId: string,
        payload: { id: string; name: { fr: string; en: string } },
    ): Promise<AxiosResponse<AnalysisCategory>> {
        const endpoint = `/analyses/categories/${categoryId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, payload);
    }

    deleteAnalysisCategory(categoryId: string): Promise<AxiosResponse<void>> {
        const endpoint = `/analyses/categories/${categoryId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    createAnalysisCategory(payload: {
        name: { fr: string; en: string };
    }): Promise<AxiosResponse<AnalysisCategory>> {
        const endpoint = '/analyses/categories';
        return this.axiosAuthentifiedRequest.post(endpoint, payload);
    }

    getTreatmentPrograms(
        networkId: string,
        queryParams?: object,
    ): Promise<AxiosResponse<TreatmentProgramInterface[]>> {
        const endpoint = `/networks/${networkId}/treatments`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }
    getSteamTreatmentPrograms(
        networkId: string,
        queryParams: object,
    ): Promise<AxiosResponse<TreatmentProgramInterface[]>> {
        const endpoint = `/networks/${networkId}/steamTreatments`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }
    addSteamTreatmentProgram(
        networkId: string,
        bodyParams: object,
    ): Promise<AxiosResponse<TreatmentProgramInterface>> {
        const endpoint = `/networks/${networkId}/steamTreatments`;
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }
    updateSteamTreatmentProgram(
        networkId: string,
        treatmentProgramId: string,
        bodyParams: object,
    ): Promise<AxiosResponse<TreatmentProgramInterface>> {
        const endpoint = `/networks/${networkId}/steamTreatments/${treatmentProgramId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }
    removeTreatmentProgram(networkId: string, treatmentProgramId: string): Promise<AxiosResponse> {
        const endpoint = `/networks/${networkId}/treatments/${treatmentProgramId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }
    removeSteamTreatmentProgram(
        networkId: string,
        treatmentProgramId: string,
    ): Promise<AxiosResponse> {
        const endpoint = `/networks/${networkId}/steamTreatments/${treatmentProgramId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    getEvents(queryParams: object): Promise<AxiosResponse<GetEventsResponse>> {
        const endpoint = '/events';
        return this.axiosAuthentifiedRequest.get(endpoint, { params: queryParams });
    }

    getEvent(eventId: string): Promise<AxiosResponse<GetEventResponse>> {
        const endpoint = `/events/${eventId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    addEvent(bodyParams: object): Promise<AxiosResponse<EventItemResponse>> {
        const endpoint = '/events';
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    updateEvent(eventId: string, bodyParams: object): Promise<AxiosResponse<EventItemResponse>> {
        const endpoint = `/events/${eventId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    deleteEvent(eventId: string, applyToSeries = false): Promise<AxiosResponse> {
        const endpoint = `/events/${eventId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint, {
            params: applyToSeries ? { applyToSeries: true } : {},
        });
    }
    getDevices(queryParams: object): Promise<AxiosResponse<DevicesResponse>> {
        const endpoint = '/deviceTypes';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    addDeviceDocumentation(
        deviceTypeId: string,
        payload: { idFile: string; title: string; lang: string },
    ): Promise<AxiosResponse> {
        const endpoint = `/deviceTypes/${deviceTypeId}/documentation`;
        return this.axiosAuthentifiedRequest.post(endpoint, payload);
    }

    updateDeviceDocumentation(
        deviceTypeId: string,
        docId: string,
        payload: { title: string; lang: string },
    ): Promise<AxiosResponse> {
        const endpoint = `/deviceTypes/${deviceTypeId}/documentation/${docId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, payload);
    }

    deleteDeviceDocumentation(deviceTypeId: string, docId: string): Promise<AxiosResponse> {
        const endpoint = `/deviceTypes/${deviceTypeId}/documentation/${docId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    getTrainingCategories(): Promise<AxiosResponse> {
        const endpoint = '/training/categories';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: { pagination: false },
        });
    }

    getTrainingVideos(params?: { search?: string; category?: string }): Promise<AxiosResponse> {
        const endpoint = '/training/videos/';
        return this.axiosAuthentifiedRequest.get(endpoint, { params });
    }

    addTrainingVideo(payload: {
        url: string;
        title: string;
        visibility: string | null; // Si null c'est une vidéo publique
        category: string;
        lang: string;
    }): Promise<AxiosResponse> {
        const endpoint = '/training/videos/';
        return this.axiosAuthentifiedRequest.post(endpoint, payload);
    }

    updateTrainingVideo(
        videoId: string,
        payload: {
            url: string;
            title: string;
            visibility: string | null; // Si null c'est une vidéo publique
            category: string;
            lang: string;
        },
    ): Promise<AxiosResponse> {
        const endpoint = `/training/videos/${videoId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, payload);
    }

    deleteTrainingVideo(videoId: string): Promise<AxiosResponse> {
        const endpoint = `/training/videos/${videoId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    getPrivacyPolicy(): Promise<AxiosResponse> {
        const endpoint = '/privacyPolicy';
        return this.axiosRequest.get(endpoint);
    }

    updatePrivacyPolicy(payload: { [lang: string]: string }): Promise<AxiosResponse> {
        const endpoint = '/privacyPolicy';
        return this.axiosAuthentifiedRequest.patch(endpoint, payload);
    }

    getSiteAnalysisOverview(siteId: string, queryParams: AnalysisOverviewQueryParams) {
        const endpoint = `/sites/${siteId}/analysisOverview`;
        return this.axiosAuthentifiedRequest.get<GetSiteAnalysisOverviewRes[]>(endpoint, {
            params: queryParams,
        });
    }

    //     Current billing summary
    getCurrentBilling(
        payload?: GetCurrentBillingParams | GetCurrentBillingExportPayload,
    ): Promise<AxiosResponse<CurrentBillingSummaryItem[] | GetCurrentBillingExportRes>> {
        const endpoint = '/pricing/currentBillingSummary';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: payload || {},
        });
    }

    getCurrentBillingDetails(
        type: 'usersDetails' | 'sitesDetails',
        teamId: string,
        queryParams: CurrentBillingDetailsParams,
    ): Promise<AxiosResponse<GetCurrentBillingDetailsRes>> {
        const endpoint = `/pricing/currentBillingSummary/${type}/${teamId}`;
        return this.axiosAuthentifiedRequest.get(endpoint, { params: queryParams });
    }
}

const $rq = new AqAjaxRequests();

export default $rq;
